import RecordComments from '../comms/RecordComments'   // Communication
import { FileImg, signUrl, signUrls, fileProblem, fileSigned, forgetFile } from '../lib/fileAccess'
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import html2pdf from 'html2pdf.js'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { useAuth } from '../hooks/useAuth'
import DateField from '../components/DateField'
import TodoFilterBar, { PillSelect, TodoFilterChips, filterChips } from '../components/TodoFilterBar'
import TodoHistory from '../components/TodoHistory'
import HistoryLink from '../components/HistoryLink'
import { loadLeaflet } from '../lib/leaflet'
import { paletteFor } from '../lib/gaZones'
import { downscale } from '../lib/img'
import { pdfToImages } from '../lib/pdfjs'
import { cropImage } from '../lib/crop'
import { todayLocal } from '../lib/dates'
import { shipTodayAuto } from '../lib/shipTime'
import { filePath, isUuid, storedPath } from '../lib/filePaths'
import { isSystemList } from '../lib/todoLists'
import { readActive } from '../lib/api'
import { isSearchShortcut, isTyping } from '../lib/recordSearch'
import { buildXlsx } from '../lib/xlsx'
import { TODO_TYPES, TYPE_TABS, DEPARTMENTS, RESPONSIBLE_ROLES, EMPTY_FILTERS, applyFilters, filtersActive, loadFilters, saveFilters, bulkPatch, closedPatch, exportRows, toCsv, exportName, statusOf, typeOf, locationOf, dayOf, missingWorklist, MISSING_HINT, NEW_FIELDS, groupByDate, summaryCounts, initialsOf } from '../lib/todoWork'
import { escHtml } from '../lib/escHtml'

const PRIORITY = { 3: { label: 'High', color: '#C93B47' }, 2: { label: 'Medium', color: '#1B4FD8' }, 1: { label: 'Low', color: '#7D8AA0' }, 0: { label: 'None', color: '#CDD4DF' } }
const Flag = ({ color, size = 14 }) => <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}><path d="M3 1.2v11.6" stroke={color} strokeWidth="1.6" strokeLinecap="round" /><path d="M3.4 1.8h7.9l-2 3 2 3H3.4z" fill={color} /></svg>
const AVATAR = ['#1B4FD8', '#3D6BE8', '#0F339B', '#2F6FB0', '#5478E4']
const avatarColor = name => AVATAR[[...String(name)].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR.length]
const CARD_ICON = {
  overdue: <><circle cx="8" cy="8" r="5.8" /><path d="M8 4.8v3.4l2.2 1.4" /></>,
  today: <><rect x="2.5" y="3.5" width="11" height="10" rx="2" /><path d="M2.5 6.8h11M5.5 2v3M10.5 2v3" /></>,
  defects: <><path d="M8 2.2l6 10.6H2z" /><path d="M8 6.6v3M8 11.2v.01" /></>,
  high: <><path d="M4 14V2" /><path d="M4 2.6h8.4l-2 3 2 3H4" /></>,
}

// PDF table cells: the app-wide td rule (no wrap, 180 px) must not cut text on paper. Short values stay on one line, text wraps at
// words, the title also breaks a very long word and gives way first
const PDF_CELL = 'max-width:none;overflow:visible;'
const PDF_ONE = PDF_CELL + 'white-space:nowrap;', PDF_TEXT = PDF_CELL + 'white-space:normal;overflow-wrap:break-word;', PDF_LONG = PDF_CELL + 'white-space:normal;overflow-wrap:anywhere;'
const PDF_SHORT = ['Type', 'Created', 'Due', 'Closed', 'Status', 'Priority', 'Days open']
const pdfCell = h => (h === 'Title' ? PDF_LONG : PDF_SHORT.includes(h) ? PDF_ONE : PDF_TEXT)

// GA plan pins: open red, closed green
const pinColor = p => (p.done ? '#137050' : '#C93B47')

function todayStr() { return shipTodayAuto() }
function fmtLong(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
function inNextDays(dateStr, n) {
  if (!dateStr) return false
  const d = new Date(dateStr + 'T00:00:00')
  const today = new Date(todayStr() + 'T00:00:00')
  const diff = (d - today) / 86400000
  return diff >= 0 && diff <= n
}
function isOverdue(dateStr, done) {
  if (!dateStr || done) return false
  return new Date(dateStr + 'T00:00:00') < new Date(todayStr() + 'T00:00:00')
}
function fmtDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  const today = new Date(todayStr() + 'T00:00:00')
  const diff = Math.round((d - today) / 86400000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
function nextDue(dateStr, recurrence) {
  const d = new Date(dateStr + 'T00:00:00')
  if (recurrence === 'daily') d.setDate(d.getDate() + 1)
  else if (recurrence === 'weekly') d.setDate(d.getDate() + 7)
  else if (recurrence === 'monthly') d.setMonth(d.getMonth() + 1)
  else return null
  return todayLocal(d)
}

export default function ToDo() {
  const { config, can } = useAuth()
  const [lists, setLists] = useState([])
  const [folders, setFolders] = useState([])
  const [collapsed, setCollapsed] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem('todoCollapsed') || '[]')) } catch { return new Set() } })
  async function loadFolders() {
    const { data } = await supabase.from('todo_folders').select('*').eq('client_id', config.id).order('position', { ascending: true }).order('created_at', { ascending: true })
    setFolders(data || [])
  }
  const [sharePopover, setSharePopover] = useState(null) // listId
  const [allItems, setAllItems] = useState([])      // all items across lists (for smart views)
  const [view, setView] = useState('today')         // today | next7 | all | list:<id> | history (closed items)
  const [loading, setLoading] = useState(true)
  const [newListName, setNewListName] = useState('')
  const [newItem, setNewItem] = useState('')
  const [busy, setBusy] = useState(false)
  const [detailItem, setDetailItem] = useState(null)
  const [subTasks, setSubTasks] = useState([])
  const [subInput, setSubInput] = useState('')
  const [crew, setCrew] = useState([])
  const [sortBy, setSortBy] = useState('priority')   // priority | due
  const [filterHigh, setFilterHigh] = useState(false)
  const [filterAssignee, setFilterAssignee] = useState('')
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() } })
  const [decks, setDecks] = useState([])
  const [activeDeck, setActiveDeck] = useState(null)
  const [pinMode, setPinMode] = useState(false)
  const [placingItem, setPlacingItem] = useState(null)
  const [zones, setZones] = useState([])
  const [zoneMode, setZoneMode] = useState(false)
  const [lsaItems, setLsaItems] = useState([])
  const [showLsa, setShowLsa] = useState(false)
  const [mapping, setMapping] = useState(false)
  const [mapResult, setMapResult] = useState(null)
  const [placingLsa, setPlacingLsa] = useState(null)
  const [deckUploading, setDeckUploading] = useState(false)
  const [printMode, setPrintMode] = useState(false)
  const [cropMode, setCropMode] = useState(false)
  const [exporting, setExporting] = useState(false)
  const deckFileRef = useRef(null)
  const [comments, setComments] = useState([])
  const [commentInput, setCommentInput] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const fileRef = useRef(null)
  const savedTimer = useRef(null)
  // worklist (sql/todo-worklists.sql): filters remembered per login, components = active equipment only
  const who = config?.crewName || config?.email
  const [filters, setFilters] = useState(() => loadFilters(who))
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }))
  useEffect(() => { saveFilters(who, filters) }, [filters, who])
  const [equipment, setEquipment] = useState([])        // active equipment: the component picker
  const [eqNames, setEqNames] = useState({})             // id -> name, also archived equipment an item is still linked to
  const [selecting, setSelecting] = useState(false)
  const [selected, setSelected] = useState(() => new Set())
  const [bulk, setBulk] = useState(null)                 // the "Update tasks" form
  const [planIds, setPlanIds] = useState(null)           // "View plan" from a filtered list: only these pins
  const [exportOpen, setExportOpen] = useState(false)
  const [itemsReady, setItemsReady] = useState(false)    // first load finished: skeleton rows until then, never a spinner
  const [dateFilter, setDateFilter] = useState('')       // the Overdue / Due today cards
  const [listsOpen, setListsOpen] = useState(false)      // the Lists dropdown
  const [drawerBox, setDrawerBox] = useState(null)       // where the content area sits: the drawer never covers the app's menu or header
  const pageRef = useRef(null), listsRef = useRef(null), addRef = useRef(null), titleRef = useRef(null), listsByDrag = useRef(false)

  useEffect(() => { if (config?.id) { loadLists(); loadFolders(); Promise.resolve(loadAllItems()).catch(() => {}).finally(() => setItemsReady(true)); loadCrew(); loadEquipment() } }, [config?.id])
  // opened from the Dashboard ("open Defects · Engineering"): those filters, every list
  useEffect(() => {
    try {
      const want = JSON.parse(localStorage.getItem('hc_todo_filter') || 'null')
      if (want && typeof want === 'object') { localStorage.removeItem('hc_todo_filter'); setFilters({ ...EMPTY_FILTERS, ...want }); setView('all') }
    } catch (e) { /* nothing asked */ }
  }, [])
  useEffect(() => { setSelected(new Set()); if (view !== 'plan') setPlanIds(null) }, [view])
  useEffect(() => { try { localStorage.setItem('todoCollapsed', JSON.stringify([...collapsed])) } catch {} }, [collapsed])

  useEffect(() => {
    if (!config?.id) return
    supabase.from('ga_decks').select('*').eq('client_id', config.id).order('sort_order')
      .then(({ data }) => { setDecks(data || []); setActiveDeck(d => d || (data || [])[0] || null) })
  }, [config?.id])

  async function exportSnagPdf() {
    if (!decks.length) return
    const signed = await signUrls(decks.map(d => d.image_url))   // private bucket: the plan images through signed links
    const vessel = (config && config.vessel_name) || 'Vessel'
    const BLUE = '#1B4FD8', LINE = '#E5E9F1', MUTE = '#67748C', TEXT = '#16233B'
    const open = allItems.filter(i => i.deck_id && i.pin_x != null && !i.done).length
    const closed = allItems.filter(i => i.deck_id && i.pin_x != null && i.done).length

    let body = ''
    decks.forEach(dk => {
      const dp = allItems.filter(i => i.deck_id === dk.id && i.pin_x != null)
      if (!dp.length) return
      const pins = dp.map((p, i) => {
        const col = pinColor(p)
        return `<div style="position:absolute;left:${(p.pin_x * 100).toFixed(2)}%;top:${(p.pin_y * 100).toFixed(2)}%;transform:translate(-50%,-50%);width:17px;height:17px;border-radius:50%;border:2px solid #fff;background:${col};color:#fff;font-size:9px;font-weight:700;line-height:13px;text-align:center;">${i + 1}</div>`
      }).join('')
      const rows = dp.map((p, i) => {
        const zn = escHtml(zones.find(z => z.id === p.zone_id)?.name) || '&mdash;'
        const st = p.done ? '<span style="color:#137050;font-weight:600;">Closed</span>' : '<span style="color:#C93B47;font-weight:600;">Open</span>'
        return `<tr style="border-bottom:1px solid ${LINE};">
          <td style="${PDF_ONE}padding:5px 7px;font-weight:700;">${i + 1}</td>
          <td style="${PDF_LONG}padding:5px 7px;">${escHtml(p.text || '')}</td>
          <td style="${PDF_TEXT}padding:5px 7px;color:${MUTE};">${zn}</td>
          <td style="${PDF_ONE}padding:5px 7px;">${PRIORITY[p.priority || 0]?.label || '&mdash;'}</td>
          <td style="${PDF_TEXT}padding:5px 7px;color:${MUTE};">${escHtml(p.assignee) || '&mdash;'}</td>
          <td style="${PDF_ONE}padding:5px 7px;">${st}</td></tr>`
      }).join('')
      body += `<div style="margin-bottom:22px;page-break-inside:avoid;">
        <div style="font-size:12px;font-weight:700;color:${BLUE};margin-bottom:7px;">${escHtml(dk.name)}</div>
        <div style="position:relative;border:1px solid ${LINE};border-radius:5px;overflow:hidden;">
          <img src="${escHtml(signed[dk.image_url] || '')}" style="width:100%;display:block;" />${pins}
        </div>
        <table style="width:100%;border-collapse:collapse;margin-top:9px;font-size:9.5px;color:${TEXT};">
          <thead><tr style="background:#F3F5F9;color:${MUTE};text-align:left;">
            <th style="padding:5px 7px;width:24px;">#</th><th style="padding:5px 7px;">Item</th>
            <th style="padding:5px 7px;width:90px;">Zone</th><th style="padding:5px 7px;width:60px;">Priority</th>
            <th style="padding:5px 7px;width:90px;">Assigned</th><th style="padding:5px 7px;width:52px;">Status</th>
          </tr></thead><tbody>${rows}</tbody></table></div>`
    })

    const html = `<div style="width:960px;padding:6px 10px;font-family:Inter,Helvetica,Arial,sans-serif;color:${TEXT};background:#fff;">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:10px;">
        <div><div style="font-size:19px;font-weight:800;letter-spacing:.12em;color:${BLUE};">HEYCAPTAIN</div>
          <div style="font-size:9px;color:${MUTE};letter-spacing:.18em;text-transform:uppercase;">Snag List</div></div>
        <div style="text-align:right;font-size:9.5px;color:${MUTE};line-height:1.7;">
          <div style="font-size:13px;font-weight:700;color:${TEXT};">${escHtml(vessel)}</div><div>${fmtLong(todayLocal())}</div></div>
      </div>
      <div style="background:${BLUE};color:#fff;padding:6px 11px;font-size:10px;letter-spacing:.18em;text-transform:uppercase;margin:0 0 16px;">
        ${open} open &middot; ${closed} closed</div>
      ${body}
      <div style="margin-top:12px;padding-top:9px;border-top:1px solid ${LINE};display:flex;justify-content:space-between;font-size:8.5px;color:${MUTE};">
        <span style="font-weight:800;letter-spacing:.12em;color:${BLUE};">HEYCAPTAIN</span>
        <span>${escHtml(vessel)} &middot; Snag List &middot; ${fmtLong(todayLocal())}</span></div>
    </div>`

    const el = document.createElement('div')
    el.innerHTML = html
    document.body.appendChild(el)
    const fname = ('Snag_List_' + vessel + '_' + todayLocal()).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    const opt = { margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, windowWidth: 960, width: 960 },
      pagebreak: { mode: ['legacy'] }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
    html2pdf().set(opt).from(el).save().then(() => document.body.removeChild(el))
  }

  async function addDecks(files) {
    if (!files?.length || !config?.id) return
    setDeckUploading(true)
    const created = []
    const expanded = []
    for (const f of files) {
      if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) {
        try { expanded.push(...await pdfToImages(f)) }
        catch (e) { alert('Could not read ' + f.name + ': ' + e.message) }
      } else expanded.push(f)
    }
    for (const raw of expanded) {
      const f = await downscale(raw, 2600, 0.9)
      const dims = await new Promise(res => {
        const img = new Image()
        img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight })
        img.onerror = () => res(null)
        img.src = URL.createObjectURL(f)
      })
      if (!dims) continue
      const ext = (f.name.split('.').pop() || 'png').toLowerCase()
      const path = filePath('ga', config.id, Math.random().toString(36).slice(2, 7) + '.' + ext)
      const { error } = await supabase.storage.from('crew-docs').upload(path, f, { contentType: f.type || 'image/png' })
      if (error) continue
      const name = f.name.replace(/\.\w+$/, '').replace(/[-_]+/g, ' ').trim() || 'Deck'
      const { data } = await supabase.from('ga_decks').insert({
        client_id: config.id, name, image_url: path,   // the PATH (private bucket)
        width_px: dims.w, height_px: dims.h, sort_order: (decks.length + created.length + 1),
      }).select().single()
      if (data) created.push(data)
    }
    if (created.length) {
      setDecks(d => [...d, ...created])
      setActiveDeck(a => a || created[0])
    }
    setDeckUploading(false)
  }

  async function cropDeck(srcDeck, box, name) {
    setDeckUploading(true)
    try {
      const file = await cropImage((await signUrl(srcDeck.image_url)) || srcDeck.image_url, box, name)
      const dims = await new Promise(res => {
        const img = new Image()
        img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight })
        img.onerror = () => res(null)
        img.src = URL.createObjectURL(file)
      })
      if (!dims) throw new Error('Could not read the cropped image')
      const path = filePath('ga', config.id, Math.random().toString(36).slice(2, 7) + '.png')
      const { error } = await supabase.storage.from('crew-docs').upload(path, file, { contentType: 'image/png' })
      if (error) throw error
      const { data } = await supabase.from('ga_decks').insert({
        client_id: config.id, name, image_url: path,   // the PATH (private bucket)
        width_px: dims.w, height_px: dims.h, sort_order: decks.length + 1,
      }).select().single()
      if (data) { setDecks(d => [...d, data]); setActiveDeck(data); setCropMode(false) }
    } catch (e) { alert('Crop failed: ' + e.message) }
    setDeckUploading(false)
  }

  async function renameDeck(id, name) {
    setDecks(d => d.map(x => x.id === id ? { ...x, name } : x))
    setActiveDeck(a => a && a.id === id ? { ...a, name } : a)
    await supabase.from('ga_decks').update({ name }).eq('id', id)
  }

  // confirm-only (asked at the button): the pins on it are cleared, which an Undo cannot put back
  async function deleteDeck(id) {
    setDecks(d => d.filter(x => x.id !== id))
    setActiveDeck(a => a && a.id === id ? null : a)
    await supabase.from('ga_decks').delete().eq('id', id)
  }

  async function moveLsa(id, px, py) {
    setLsaItems(list => list.map(i => i.id === id ? { ...i, pin_x: px, pin_y: py } : i))
    await supabase.from('lsa_items').update({ pin_x: px, pin_y: py }).eq('id', id)
  }

  async function placeLsa(deckId, px, py) {
    if (!placingLsa) return
    const patch = { deck_id: deckId, pin_x: px, pin_y: py }
    setLsaItems(list => list.map(i => i.id === placingLsa.id ? { ...i, ...patch } : i))
    await supabase.from('lsa_items').update(patch).eq('id', placingLsa.id)
    setPlacingLsa(null)
  }

  async function autoPlaceLsa() {
    const unplaced = lsaItems.filter(i => i.pin_x == null && i.location)
    if (!unplaced.length) { alert('Every LSA item with a location is already placed.'); return }
    if (!zones.length) { alert('Draw some zones first.'); return }
    setMapping(true)
    try {
      const locs = [...new Set(unplaced.map(i => i.location.trim()))]
      const zl = zones.map(z => ({ name: z.name, deck: decks.find(d => d.id === z.deck_id)?.name || '' }))
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
        body: JSON.stringify({ action: 'map_locations', locations: locs, zones: zl }),
      })
      const text = await resp.text()
      console.log('MAP_LOCATIONS status', resp.status, text.slice(0, 800))
      if (!resp.ok) throw new Error('AI error ' + resp.status + ': ' + text.slice(0, 200))
      let out
      try { out = JSON.parse(text) } catch (pe) { throw new Error('Bad JSON: ' + text.slice(0, 200)) }
      if (out.error) throw new Error(out.error + (out.raw ? ' — ' + out.raw.slice(0, 150) : ''))
      const rows = (out.map || []).map(m => {
        const zone = m.zone ? zones.find(z => z.name.toLowerCase() === String(m.zone).toLowerCase()) : null
        const n = unplaced.filter(i => i.location.trim().toLowerCase() === String(m.location).toLowerCase()).length
        return { location: m.location, zone, count: n, accept: !!zone }
      }).filter(r => r.count > 0)
      setMapResult(rows)
    } catch (e) { alert('Auto-place failed: ' + e.message) }
    setMapping(false)
  }

  async function applyMapping() {
    const rows = (mapResult || []).filter(r => r.accept && r.zone)
    let done = 0
    for (const r of rows) {
      const z = r.zone
      const patch = {
        deck_id: z.deck_id, zone_id: z.id,
        pin_x: z.x + z.w / 2, pin_y: z.y + z.h / 2,
      }
      const ids = lsaItems.filter(i => i.pin_x == null && i.location &&
        i.location.trim().toLowerCase() === String(r.location).toLowerCase()).map(i => i.id)
      if (!ids.length) continue
      const { error } = await supabase.from('lsa_items').update(patch).in('id', ids)
      if (!error) {
        done += ids.length
        setLsaItems(list => list.map(i => ids.includes(i.id) ? { ...i, ...patch } : i))
      }
    }
    setMapResult(null)
    setShowLsa(true)
    alert(done + ' LSA items placed on the plan.')
  }

  async function loadZones() {
    if (!config?.id) return
    const { data } = await supabase.from('ga_zones').select('*').eq('client_id', config.id)
    setZones(data || [])
  }
  useEffect(() => { loadZones() }, [config?.id])

  useEffect(() => {
    if (!config?.id) return
    supabase.from('lsa_items').select('id,name,category,location,deck_id,zone_id,pin_x,pin_y,expiry_date')
      .eq('client_id', config.id)
      .then(({ data }) => setLsaItems(data || []))
  }, [config?.id])

  async function createZone(deckId, name, box) {
    const { data } = await supabase.from('ga_zones').insert({
      client_id: config.id, deck_id: deckId, name, x: box.x, y: box.y, w: box.w, h: box.h,
    }).select().single()
    if (data) setZones(z => [...z, data])
  }
  async function updateZone(id, patch) {
    setZones(z => z.map(x => x.id === id ? { ...x, ...patch } : x))
    await supabase.from('ga_zones').update(patch).eq('id', id)
  }
  // asked at the button; Undo for 10 seconds (src/lib/undoDelete.js)
  async function deleteZone(id) {
    const z = zones.find(x => x.id === id)
    setZones(zs => zs.filter(x => x.id !== id))
    try { await deleteWithUndo(supabase, { table: 'ga_zones', id, label: 'zone "' + ((z && z.name) || 'zone') + '"', onUndone: () => loadZones() }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); loadZones() }
  }

  async function placePin(deckId, px, py) {
    if (!placingItem) return
    const patch = { deck_id: deckId, pin_x: px, pin_y: py }
    setAllItems(its => its.map(x => x.id === placingItem.id ? { ...x, ...patch } : x))
    setDetailItem(d => d && d.id === placingItem.id ? { ...d, ...patch } : d)
    await supabase.from('todo_items').update(patch).eq('id', placingItem.id)
    setPlacingItem(null)
  }

  async function movePin(id, px, py) {
    setAllItems(its => its.map(x => x.id === id ? { ...x, pin_x: px, pin_y: py } : x))
    setDetailItem(d => d && d.id === id ? { ...d, pin_x: px, pin_y: py } : d)
    await supabase.from('todo_items').update({ pin_x: px, pin_y: py }).eq('id', id)
  }

  async function createPin(deckId, px, py) {
    const targetList = activeListId || await ensureInbox()
    if (!targetList) { alert('Create a list first'); return null }
    const me = config?.crewName || config?.email
    const { data } = await supabase.from('todo_items').insert({
      list_id: targetList, text: 'New snag', done: false, priority: 2,
      created_by: me, position: allItems.length,
      deck_id: deckId, pin_x: px, pin_y: py,
    }).select().single()
    if (data) { setAllItems(it => [...it, data]); setDetailItem(data) }
    return data
  }

  // the component picker: active equipment only (archived never offered); names of archived equipment an item is still
  // linked to are read by id, so the item keeps showing it
  async function loadEquipment() {
    try {
      const { data } = await readActive('equipment', () => supabase.from('equipment').select('id,uid,name,department,location').eq('client_id', config.id).order('name'))
      setEquipment(data || [])
      setEqNames(n => ({ ...n, ...Object.fromEntries((data || []).map(e => [e.id, e.name || e.uid || 'Equipment'])) }))
    } catch (e) { setEquipment([]) }
  }
  useEffect(() => {
    const missing = [...new Set(allItems.map(i => i.equipment_id).filter(id => id && !eqNames[id]))]
    if (!missing.length) return
    supabase.from('equipment').select('id,uid,name').in('id', missing).then(({ data }) => {
      const got = Object.fromEntries((data || []).map(e => [e.id, (e.name || e.uid || 'Equipment') + ' (archived)']))
      for (const id of missing) if (!got[id]) got[id] = '(removed equipment)'
      setEqNames(n => ({ ...n, ...got }))
    }, () => {})
  }, [allItems]) // eslint-disable-line
  const equipmentName = id => (id ? eqNames[id] || '' : '')
  const zoneName = id => zones.find(z => z.id === id)?.name || ''

  async function loadCrew() {
    try {
      // also pull crew sub-accounts
      const { data: accounts } = await supabase.from('crew_accounts').select('name').eq('client_id', config.id).eq('active', true)
      const accountNames = (accounts || []).map(a => a.name).filter(Boolean)
      // Only people with login accounts can see a shared list, so only they are shareable targets.
      setCrew([...new Set(accountNames)])
    } catch (e) { setCrew([]) }
  }

  async function loadLists() {
    setLoading(true)
    const { data } = await supabase.from('todo_lists').select('*').eq('client_id', config.id).order('position', { ascending: true }).order('created_at', { ascending: true })
    const all = data || []
    const me = config?.crewName || config?.email
    const myRole = config?.role || 'captain'
    const myName = config?.crewName || config?.vessel_name || null
    const myEmail = config?.email || null
    // Everyone (captain and crew alike) sees only: lists they created,
    // lists shared with their role or their name/email, or lists where they
    // have an assigned task. Private lists stay private in both directions.
    const mine = [myName, myEmail].filter(Boolean)
    const { data: myItems } = await supabase.from('todo_items').select('list_id').eq('assignee', me)
    const assignedListIds = new Set((myItems || []).map(i => i.list_id))
    const shareMatch = (sw) => {
      const arr = sw || []
      return arr.includes(myRole) || mine.some(x => arr.includes(x))
    }
    // + the shared system lists (Inbox, Defects, Renewals, LSA / FFE, Refit, ISM Findings) for everyone with To Do view,
    //   whoever created them; + for the Captain / management any list the AI / database made (no creator)
    const boss = config?.accountType !== 'crew'
    setLists(all.filter(l =>
      mine.includes(l.created_by) ||
      shareMatch(l.shared_with) ||
      assignedListIds.has(l.id) ||
      isSystemList(l.name) ||
      (boss && !l.created_by)
    ))
    setLoading(false)
  }

  async function saveShare(listId, sharedWith) {
    await supabase.from('todo_lists').update({ shared_with: sharedWith }).eq('id', listId)
    setLists(ls => ls.map(l => l.id === listId ? { ...l, shared_with: sharedWith } : l))
  }

  function isSharedWith(list, roleOrName) {
    return (list.shared_with || []).includes(roleOrName)
  }

  async function loadAllItems() {
    const { data: ls } = await supabase.from('todo_lists').select('id,name').eq('client_id', config.id)
    const listIds = (ls || []).map(l => l.id)
    if (!listIds.length) { setAllItems([]); return }
    const systemIds = new Set((ls || []).filter(l => isSystemList(l.name)).map(l => l.id))
    const { data } = await supabase.from('todo_items').select('*').in('list_id', listIds).order('created_at', { ascending: true })
    const me = config?.crewName || config?.email
    const filtered = (data || []).filter(i =>
      i.created_by === me ||
      i.assignee === me ||
      !i.created_by ||
      systemIds.has(i.list_id)
    )
    setAllItems(filtered)
    // opened from the header search: that task's details (once)
    const want = localStorage.getItem('hc_open_todo')
    if (want) { localStorage.removeItem('hc_open_todo'); const hit = filtered.find(i => String(i.id) === want); if (hit) setDetailItem(hit) }
  }

  function listName(id) { return lists.find(l => l.id === id)?.name || '' }

  // ---- which items to show based on view ----
  const activeListId = view.startsWith('list:') ? view.slice(5) : null
  const activeFolderId = view.startsWith('folder:') ? view.slice(7) : null
  let shownItems = []
  let viewTitle = ''
  if (view === 'today') { shownItems = allItems.filter(i => !i.done && (i.due_date === todayStr() || isOverdue(i.due_date, i.done))); viewTitle = 'Today' }
  else if (view === 'next7') { shownItems = allItems.filter(i => !i.done && inNextDays(i.due_date, 7)); viewTitle = 'Next 7 Days' }
  else if (view === 'all') { shownItems = allItems; viewTitle = 'All Tasks' }   // the Status control decides (default Open)
  else if (view === 'history') { viewTitle = 'History' }   // TodoHistory draws it
  else if (view === 'mine') { const me = config?.crewName; shownItems = me ? allItems.filter(i => !i.done && i.assignee === me) : []; viewTitle = 'My Tasks' }
  else if (activeListId) { shownItems = allItems.filter(i => i.list_id === activeListId); viewTitle = listName(activeListId) }
  else if (activeFolderId) { const fLists = new Set(lists.filter(l => l.folder_id === activeFolderId).map(l => l.id)); shownItems = allItems.filter(i => !i.done && fLists.has(i.list_id)); viewTitle = (folders.find(f => f.id === activeFolderId)?.name || 'Folder') }
  // apply filters
  if (dateFilter === 'overdue') shownItems = shownItems.filter(i => isOverdue(i.due_date, i.done))
  else if (dateFilter === 'today') shownItems = shownItems.filter(i => !i.done && i.due_date === todayStr())
  if (filterHigh) shownItems = shownItems.filter(i => i.priority === 3)
  if (filterAssignee) shownItems = shownItems.filter(i => i.assignee === filterAssignee)
  // the worklist filters bar (search, department, component, list, location, status, type)
  // (an open list / folder is the list filter: a remembered List choice does not hide its items)
  // (Completed is closed items whatever the Status control says)
  const viewFilters = { ...filters, ...(activeListId || activeFolderId ? { list: '' } : {}) }
  // the type tabs count what the other filters leave
  const preType = applyFilters(shownItems, { ...viewFilters, type: '' }, { list: listName, equipment: equipmentName, zone: zoneName })
  const typeTabs = TYPE_TABS.map(([v, label]) => ({ value: v, label, count: v ? preType.filter(i => typeOf(i) === v).length : preType.length }))
  shownItems = viewFilters.type ? preType.filter(i => typeOf(i) === viewFilters.type) : preType
  // sort (Manual is for a list: leaving the list falls back to Priority)
  const sortNow = sortBy === 'manual' && !activeListId ? 'priority' : sortBy
  if (sortNow === 'manual') shownItems = [...shownItems].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  else if (sortNow === 'priority') shownItems = [...shownItems].sort((a, b) => (b.priority - a.priority) || ((a.due_date || '9999') > (b.due_date || '9999') ? 1 : -1))
  else shownItems = [...shownItems].sort((a, b) => ((a.due_date || '9999') > (b.due_date || '9999') ? 1 : -1) || (b.priority - a.priority))

  const counts = {
    mine: config?.accountType === 'crew' ? allItems.filter(i => { const me = config?.crewName; return me && !i.done && i.assignee === me }).length : 0,
    today: allItems.filter(i => !i.done && (i.due_date === todayStr() || isOverdue(i.due_date, i.done))).length,
    next7: allItems.filter(i => !i.done && inNextDays(i.due_date, 7)).length,
    all: allItems.filter(i => !i.done).length,
    completed: allItems.filter(i => i.done).length,
    pins: allItems.filter(i => !i.done && i.deck_id && i.pin_x != null).length,
  }
  function listCount(id) { return allItems.filter(i => i.list_id === id && !i.done).length }

  // ---- worklist: bulk update, export, view plan ----
  const toggleSel = id => setSelected(sel => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); return n })
  async function applyBulk() {
    const ids = [...selected], patch = bulkPatch(bulk)
    if (!ids.length || !Object.keys(patch).length) { setBulk(null); return }
    setBusy(true)
    const { error } = await supabase.from('todo_items').update(patch).in('id', ids)
    setBusy(false)
    if (error) { alert(missingWorklist(error.message) ? MISSING_HINT : 'Could not update: ' + error.message); return }
    const now = new Date().toISOString()
    setAllItems(its => its.map(x => ids.includes(x.id) ? { ...x, ...patch, ...('done' in patch && patch.done !== x.done ? closedPatch(x, patch.done, now) : {}) } : x))
    setBulk(null); setSelected(new Set()); setSelecting(false)
  }
  const exportNames = { list: listName, equipment: equipmentName, zone: zoneName }
  // the choices of the filter pills (To Do and History)
  const filterOptions = {
    departments: [...new Set([...DEPARTMENTS, ...allItems.map(i => i.department).filter(Boolean)])].map(d => ({ value: d, label: d })),
    components: [...new Set(allItems.map(i => i.equipment_id).filter(Boolean))].map(id => ({ value: id, label: equipmentName(id) || 'Equipment' })).sort((a, b) => a.label.localeCompare(b.label)),
    lists: lists.map(l => ({ value: l.id, label: l.name })),
    locations: [...new Set(allItems.map(i => locationOf(i, zoneName)).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(l => ({ value: l, label: l })),
  }
  function download(blob, name) {
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 4000)
  }
  async function exportList(kind) {
    setExportOpen(false)
    const rows = exportRows(shownItems, exportNames), vessel = config?.vessel_name || 'Vessel'
    if (kind === 'csv') return download(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }), exportName(vessel, viewTitle, 'csv'))
    if (kind === 'xlsx') {
      const bytes = await buildXlsx([{ name: viewTitle || 'Worklist', rows, widths: [48, 12, 24, 14, 20, 22, 11, 11, 11, 9, 9, 20] }])
      return download(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), exportName(vessel, viewTitle, 'xlsx'))
    }
    exportListPdf(rows, viewTitle, [filters.department, filters.type && filters.type + 's', filters.status !== 'all' && (filters.status === 'open' ? 'Open' : 'Closed')].filter(Boolean).join(' \u00b7 '))
  }
  // History export ("Engineering defects closed in October 2026"): Excel or PDF of the rows shown
  async function exportHistory(kind, rows, title) {
    const vessel = config?.vessel_name || 'Vessel'
    if (kind === 'xlsx') {
      const bytes = await buildXlsx([{ name: 'History', rows: [[title], [], ...rows], widths: [11, 48, 12, 14, 24, 20, 22, 22, 10] }])
      return download(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), exportName(vessel, title, 'xlsx'))
    }
    exportListPdf(rows, title, '')
  }
  // a list as a PDF for the yard / management (the GA plan has its own snag-list PDF); rows[0] = the header
  function exportListPdf(rows, title, sub) {
    const vessel = (config && config.vessel_name) || 'Vessel'
    const BLUE = '#1B4FD8', LINE = '#E5E9F1', MUTE = '#67748C', TEXT = '#16233B'
    const esc = v => String(v == null ? '' : v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    const [head, ...body] = rows
    const st = head.indexOf('Status')
    const open = body.filter(r => r[st] === 'Open').length
    const html = `<div style="width:1120px;padding:6px 10px;font-family:Inter,Helvetica,Arial,sans-serif;color:${TEXT};background:#fff;">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:10px;">
        <div><div style="font-size:19px;font-weight:800;letter-spacing:.12em;color:${BLUE};">HEYCAPTAIN</div>
          <div style="font-size:9px;color:${MUTE};letter-spacing:.18em;text-transform:uppercase;">Worklist</div></div>
        <div style="text-align:right;font-size:9.5px;color:${MUTE};line-height:1.7;">
          <div style="font-size:13px;font-weight:700;color:${TEXT};">${esc(vessel)}</div><div>${fmtLong(todayLocal())}</div></div>
      </div>
      <div style="background:${BLUE};color:#fff;padding:6px 11px;font-size:10px;letter-spacing:.14em;text-transform:uppercase;margin:0 0 12px;">
        ${esc(title)}${sub ? ' &middot; ' + esc(sub) : ''} &middot; ${st < 0 ? body.length + ' closed' : open + ' open &middot; ' + (body.length - open) + ' closed'}</div>
      <table style="width:100%;border-collapse:collapse;font-size:8.5px;color:${TEXT};">
        <thead><tr style="background:#F3F5F9;color:${MUTE};text-align:left;">${head.map(h => `<th style="padding:5px 5px;">${esc(h)}</th>`).join('')}</tr></thead>
        <tbody>${body.map(r => `<tr style="border-bottom:1px solid ${LINE};page-break-inside:avoid;">${r.map((c, k) => k === st
          ? `<td style="${PDF_ONE}padding:4px 5px;font-weight:600;color:${c === 'Open' ? '#C93B47' : '#137050'};">${esc(c)}</td>`
          : `<td style="${pdfCell(head[k])}padding:4px 5px;${k === 0 ? 'font-weight:600;' : 'color:' + MUTE + ';'}">${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>
      <div style="margin-top:12px;padding-top:9px;border-top:1px solid ${LINE};display:flex;justify-content:space-between;font-size:8.5px;color:${MUTE};">
        <span style="font-weight:800;letter-spacing:.12em;color:${BLUE};">HEYCAPTAIN</span>
        <span>${esc(vessel)} &middot; Worklist &middot; ${fmtLong(todayLocal())}</span></div>
    </div>`
    const el = document.createElement('div')
    el.innerHTML = html
    document.body.appendChild(el)
    const opt = { margin: 7, filename: exportName(vessel, title, 'pdf'), image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, windowWidth: 1120, width: 1120 }, pagebreak: { mode: ['css', 'legacy'] },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } }
    html2pdf().set(opt).from(el).save().then(() => document.body.removeChild(el), () => document.body.removeChild(el))
  }
  function viewPlan() {
    setPlanIds(new Set(shownItems.map(i => i.id)))
    const first = shownItems.find(i => i.deck_id && i.pin_x != null)
    if (first) { const d = decks.find(x => x.id === first.deck_id); if (d) setActiveDeck(d) }
    setView('plan'); setDetailItem(null)
  }

  async function addList() {
    const name = newListName.trim(); if (!name) return
    setBusy(true)
    const me = config?.crewName || config?.email
    const { data } = await supabase.from('todo_lists').insert({ client_id: config.id, name, position: lists.length, created_by: me }).select().single()
    setBusy(false)
    if (data) { setLists(l => [...l, data]); setView('list:' + data.id); setNewListName('') }
  }
  async function deleteList(id) {
    const l = lists.find(x => x.id === id)
    if (!confirm('Delete the list "' + ((l && l.name) || 'list') + '" and all its tasks? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'todo_lists', id, label: 'list "' + ((l && l.name) || 'list') + '"', children: [{ table: 'todo_items', column: 'list_id' }], onUndone: () => { loadLists(); loadAllItems() } }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); return }
    setLists(ls => ls.filter(l => l.id !== id))
    setAllItems(its => its.filter(i => i.list_id !== id))
    if (view === 'list:' + id) setView('today')
  }

  const [newFolderName, setNewFolderName] = useState('')
  async function addFolder() {
    const name = newFolderName.trim(); if (!name) return
    const me = config?.crewName || config?.email
    const { data } = await supabase.from('todo_folders').insert({ client_id: config.id, name, position: folders.length, created_by: me }).select().single()
    if (data) { setFolders(f => [...f, data]); setNewFolderName('') }
  }
  async function moveListToFolder(listId, folderId) {
    setLists(ls => ls.map(l => l.id === listId ? { ...l, folder_id: folderId } : l))
    await supabase.from('todo_lists').update({ folder_id: folderId }).eq('id', listId)
  }
  async function renameFolder(id, name) {
    setFolders(fs => fs.map(f => f.id === id ? { ...f, name } : f))
    await supabase.from('todo_folders').update({ name }).eq('id', id)
  }
  async function deleteFolder(id) {
    const f = folders.find(x => x.id === id), inside = lists.filter(l => l.folder_id === id).map(l => l.id)
    if (!confirm('Delete the folder "' + ((f && f.name) || 'folder') + '"? Its lists will become standalone (not deleted). You can undo it for 10 seconds.')) return
    await supabase.from('todo_lists').update({ folder_id: null }).eq('folder_id', id)
    // Undo: the folder back, and its lists back in it
    try { await deleteWithUndo(supabase, { table: 'todo_folders', id, label: 'folder "' + ((f && f.name) || 'folder') + '"', onUndone: async () => { if (inside.length) await supabase.from('todo_lists').update({ folder_id: id }).in('id', inside); loadFolders(); loadLists() } }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); loadLists(); return }
    setFolders(fs => fs.filter(f => f.id !== id))
    setLists(ls => ls.map(l => l.folder_id === id ? { ...l, folder_id: null } : l))
  }
  async function ensureInbox() {
    const me = config?.crewName || config?.email
    const isCrew = config?.accountType === 'crew'
    // Crew get a personal list they own (so RLS makes it visible to them);
    // the vessel owner gets the shared Inbox.
    const listName = isCrew ? (me ? me + "'s Tasks" : 'My Tasks') : 'Inbox'
    const existing = lists.find(l => l.name.trim().toLowerCase() === listName.toLowerCase())
    if (existing) return existing.id
    // the page only keeps the lists this login created / shares / has tasks in, so an Inbox made by the AI or the database
    // is not in `lists`: the database finds it (or makes it once) - never a second "Inbox" (sql/test-fixes-2.sql)
    const { data: id, error } = await supabase.rpc('todo_list_for', { p_client: config.id, p_name: listName })
    if (error || !id) return lists[0]?.id
    const { data: row } = await supabase.from('todo_lists').select('*').eq('id', id).maybeSingle()
    if (row) setLists(l => [row, ...l.filter(x => x.id !== id)])
    return id
  }

  async function addItem() {
    const raw = newItem.trim(); if (!raw) return
    const folderFirstList = activeFolderId ? (lists.find(l => l.folder_id === activeFolderId)?.id) : null
    setNewItem('')
    const me = config?.crewName || config?.email
    // added while the Defects tab / a department is filtered: the new item gets them (the database fills the rest)
    const extra = { ...(filters.type ? { type: filters.type } : {}), ...(filters.department ? { department: filters.department } : {}) }
    const due = view === 'today' ? todayStr() : null
    // the row shows at once and is swapped for the saved one; until then it cannot be clicked (.pending)
    const tempId = 'new-' + Math.random().toString(36).slice(2)
    setAllItems(it => [...it, { id: tempId, list_id: activeListId || folderFirstList || null, text: raw, due_date: due, priority: 0, assignee: null, done: false, created_by: me, position: it.length, photos: [], ...extra, pending: true }])
    const undoAdd = () => { setAllItems(it => it.filter(x => x.id !== tempId)); setNewItem(raw) }
    const targetList = activeListId || folderFirstList || await ensureInbox()
    if (!targetList) { undoAdd(); alert('Create a list first'); return }
    // Insert immediately with the raw text so the task appears instantly.
    const base = { list_id: targetList, text: raw, due_date: due, priority: 0, assignee: null, created_by: me, position: allItems.length }
    let { data, error } = await supabase.from('todo_items').insert({ ...base, ...extra }).select().single()
    if (error && Object.keys(extra).length && missingWorklist(error.message)) ({ data, error } = await supabase.from('todo_items').insert(base).select().single())
    if (!data) { undoAdd(); alert('Could not add the task' + (error?.message ? ': ' + error.message : '')); return }
    setAllItems(it => it.map(x => x.id === tempId ? data : x)); if (!activeListId) loadLists()
    // Then let AI enrich date/priority/assignee in the background and patch the row.
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const resp = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'todo_parse', line: raw, today: todayStr(), crew }),
        })
        if (!resp.ok) return
        const pr = await resp.json()
        const patch = {}
        if (pr.text && pr.text !== raw) patch.text = pr.text
        if (pr.due_date) patch.due_date = pr.due_date
        if ([1, 2, 3].includes(pr.priority)) patch.priority = pr.priority
        if (crew.includes(pr.assignee)) patch.assignee = pr.assignee
        if (!Object.keys(patch).length) return
        await supabase.from('todo_items').update(patch).eq('id', data.id)
        setAllItems(it => it.map(x => x.id === data.id ? { ...x, ...patch } : x))
      } catch (e) { /* raw task already saved; enrichment is best-effort */ }
    })()
  }
  async function toggleItem(item) {
    const done = !item.done
    // the database stamps / clears closed_at (sql/todo-worklists.sql); shown at once here
    const local = closedPatch(item, done)
    setAllItems(its => its.map(x => x.id === item.id ? { ...x, ...local } : x))
    setDetailItem(d => d && d.id === item.id ? { ...d, ...local } : d)
    await supabase.from('todo_items').update({ done }).eq('id', item.id)
    // when / who as the database stamped them (sql/todo-worklists.sql; before it the columns are missing: nothing to read)
    supabase.from('todo_items').select('closed_at,closed_by').eq('id', item.id).maybeSingle().then(({ data }) => {
      if (data) { setAllItems(its => its.map(x => x.id === item.id ? { ...x, ...data } : x)); setDetailItem(d => d && d.id === item.id ? { ...d, ...data } : d) }
    }, () => {})
    // recurring: when completing a recurring task, spawn the next occurrence
  if (done && item.recurrence && item.due_date) {
      const next = nextDue(item.due_date, item.recurrence)
      if (next) {
        const { data } = await supabase.from('todo_items').insert({
          list_id: item.list_id, text: item.text, due_date: next, priority: item.priority || 0,
          assignee: item.assignee || null, notes: item.notes || null, recurrence: item.recurrence, position: allItems.length
        }).select().single()
        if (data) setAllItems(its => [...its, data])
      }
    }
  }
  useEffect(() => {
    if (!detailItem?.id) { setSubTasks([]); return }
    supabase.from('todo_items').select('*').eq('parent_id', detailItem.id).order('created_at').then(({ data }) => setSubTasks(data || []))
  }, [detailItem?.id])

  useEffect(() => {
    if (!detailItem?.id) { setComments([]); return }
    supabase.from('todo_comments').select('*').eq('todo_id', detailItem.id).order('created_at')
      .then(({ data }) => setComments(data || []))
  }, [detailItem?.id])

  async function uploadPhotos(files) {
    if (!detailItem?.id || !files?.length) return
    setUploading(true)
    const urls = []
    for (const raw of files) {
      const f = await downscale(raw)
      const ext = (f.name.split('.').pop() || 'jpg').toLowerCase()
      const path = filePath('todo', config.id, Math.random().toString(36).slice(2, 8) + '.' + ext, { sub: isUuid(detailItem.id) ? detailItem.id : null })
      const { error } = await supabase.storage.from('crew-docs').upload(path, f, { upsert: false, contentType: f.type || 'image/jpeg' })
      if (!error) urls.push(path)   // the PATH (private bucket), opened through a signed link
    }
    if (urls.length) {
      const next = [...(detailItem.photos || []), ...urls]
      await updateItem(detailItem.id, { photos: next })
      await addComment(urls.length === 1 ? 'Added a photo' : `Added ${urls.length} photos`, 'photo')
    }
    setUploading(false)
  }

  async function removePhoto(url) {
    const next = (detailItem.photos || []).filter(u => u !== url)
    await updateItem(detailItem.id, { photos: next })
    const p = storedPath(url)   // the stored path, or the path inside an old link
    if (p) await supabase.storage.from('crew-docs').remove([p])
  }

  async function addComment(body, kind = 'comment') {
    const text = (body || '').trim(); if (!text || !detailItem?.id) return
    const me = config?.crewName || config?.email || 'Crew'
    const { data } = await supabase.from('todo_comments').insert({
      todo_id: detailItem.id, client_id: config.id, author: me, body: text, kind,
    }).select().single()
    if (data) setComments(c => [...c, data])
  }

  const reloadComments = todoId => supabase.from('todo_comments').select('*').eq('todo_id', todoId).order('created_at').then(({ data }) => setComments(data || []))
  async function deleteComment(id) {
    if (!confirm('Delete this comment? You can undo it for 10 seconds.')) return
    const todoId = detailItem?.id
    setComments(c => c.filter(x => x.id !== id))
    try { await deleteWithUndo(supabase, { table: 'todo_comments', id, label: 'comment', onUndone: () => { if (todoId) reloadComments(todoId) } }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); if (todoId) reloadComments(todoId) }
  }

  async function addSubTask() {
    const text = subInput.trim(); if (!text) return
    const { data } = await supabase.from('todo_items').insert({ list_id: detailItem.list_id, parent_id: detailItem.id, text, done: false, priority: 0 }).select().single()
    if (data) setSubTasks(st => [...st, data])
    setSubInput('')
  }
  async function toggleSubTask(id, done) {
    setSubTasks(st => st.map(t => t.id === id ? { ...t, done } : t))
    await supabase.from('todo_items').update({ done }).eq('id', id)
  }
  const reloadSubTasks = parentId => supabase.from('todo_items').select('*').eq('parent_id', parentId).order('created_at').then(({ data }) => setSubTasks(data || []))
  async function deleteSubTask(id) {
    const st0 = subTasks.find(t => t.id === id), parentId = detailItem?.id
    if (!confirm('Delete the sub-task "' + ((st0 && st0.text) || 'sub-task') + '"? You can undo it for 10 seconds.')) return
    setSubTasks(st => st.filter(t => t.id !== id))
    try { await deleteWithUndo(supabase, { table: 'todo_items', id, label: 'sub-task "' + ((st0 && st0.text) || 'sub-task') + '"', children: [{ table: 'todo_comments', column: 'todo_id' }], onUndone: () => { if (parentId) reloadSubTasks(parentId) } }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); if (parentId) reloadSubTasks(parentId) }
  }

  // a To Do made from an ISM finding: the delete unlinks the finding (database, on delete set null); Undo links it again
  // (sql/test-fixes-10a.sql - only to the finding it was made from). Not an ISM To Do: nothing to link, 0.
  const relinkIsm = id => supabase.rpc('ism_todo_relink', { p_todo: id }).then(() => {}, () => {})
  // asks first; Undo for 10 seconds puts the task back with its sub-tasks and comments (and its ISM finding link)
  async function deleteItem(id) {
    const it = allItems.find(x => x.id === id) || (detailItem?.id === id ? detailItem : null)
    const name = (it && it.text) || 'task'
    if (!confirm('Delete the task "' + name + '"? You can undo it for 10 seconds.')) return
    setAllItems(its => its.filter(x => x.id !== id))
    if (detailItem?.id === id) setDetailItem(null)
    try { await deleteWithUndo(supabase, { table: 'todo_items', id, label: 'task "' + name + '"', children: [{ table: 'todo_items', column: 'parent_id' }, { table: 'todo_comments', column: 'todo_id' }], onUndone: async () => { await relinkIsm(id); loadAllItems() } }) }
    catch (e) { alert('Could not delete: ' + (e.message || e)); loadAllItems() }
  }
  const [dragOverList, setDragOverList] = useState(null)
  const [dueMenu, setDueMenu] = useState(null) // item id whose date menu is open
  const [completing, setCompleting] = useState(null) // item id playing the check-off animation
  const [dragOverItem, setDragOverItem] = useState(null) // item id currently hovered as a reorder drop target
  async function reorderWithin(draggedId, targetId) {
    if (!activeListId || draggedId === targetId) return
    const ordered = [...shownItems]
    const from = ordered.findIndex(x => x.id === draggedId)
    const to = ordered.findIndex(x => x.id === targetId)
    if (from < 0 || to < 0) return
    const [moved] = ordered.splice(from, 1)
    ordered.splice(to, 0, moved)
    // reassign positions 0..n and persist
    setAllItems(all => all.map(x => { const i = ordered.findIndex(o => o.id === x.id); return i >= 0 ? { ...x, position: i } : x }))
    for (let i = 0; i < ordered.length; i++) {
      await supabase.from('todo_items').update({ position: i }).eq('id', ordered[i].id)
    }
    setSortBy('manual')
  }
  function toggleWithAnim(item) {
    if (item.done) { toggleItem(item); return } // un-completing is instant
    setCompleting(item.id)
    setTimeout(() => { toggleItem(item); setCompleting(null) }, 350)
  }
  async function cyclePriority(item) {
    const nextP = ((item.priority || 0) + 1) % 4 // 0->1->2->3->0
    setAllItems(its => its.map(x => x.id === item.id ? { ...x, priority: nextP } : x))
    setDetailItem(d => d && d.id === item.id ? { ...d, priority: nextP } : d)
    await supabase.from('todo_items').update({ priority: nextP }).eq('id', item.id)
  }
  async function setDue(item, dateISO) {
    setDueMenu(null)
    setAllItems(its => its.map(x => x.id === item.id ? { ...x, due_date: dateISO } : x))
    setDetailItem(d => d && d.id === item.id ? { ...d, due_date: dateISO } : d)
    await supabase.from('todo_items').update({ due_date: dateISO }).eq('id', item.id)
  }
  function relDateISO(days) {
    const d = new Date(todayStr() + 'T00:00:00'); d.setDate(d.getDate() + days)
    return todayLocal(d)
  }
  async function moveToList(itemId, listId) {
    setDragOverList(null)
    const it = allItems.find(x => x.id === itemId)
    if (!it || it.list_id === listId) return
    setAllItems(its => its.map(x => x.id === itemId ? { ...x, list_id: listId } : x))
    setDetailItem(d => d && d.id === itemId ? { ...d, list_id: listId } : d)
    await supabase.from('todo_items').update({ list_id: listId }).eq('id', itemId)
  }

  async function updateItem(id, patch) {
    setAllItems(its => its.map(x => x.id === id ? { ...x, ...patch } : x))
    setDetailItem(d => d && d.id === id ? { ...d, ...patch } : d)
    const { error } = await supabase.from('todo_items').update(patch).eq('id', id)
    // the worklist fields: before sql/todo-worklists.sql the column is missing; an archived component is refused
    if (error && Object.keys(patch).some(k => NEW_FIELDS.includes(k))) {
      alert(missingWorklist(error.message) ? MISSING_HINT : (error.message || 'Could not save').replace(/^todo: /, ''))
      loadAllItems(); return
    }
    setSavedFlash(true)
    if (savedTimer.current) clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => setSavedFlash(false), 1200)
  }

  // ---- views, cards, keys, drawer ----
  function goView(v) { setView(v); setDetailItem(null); setDateFilter(''); setListsOpen(false) }
  const today = todayStr()
  const summary = summaryCounts(allItems, today)
  const defectsOn = filters.type === 'Defect' && filters.status === 'open'
  const cardOn = { overdue: dateFilter === 'overdue', today: dateFilter === 'today', defects: defectsOn, high: filterHigh }
  // a card counts the whole vessel, so it opens All Tasks; a second click takes its filter off again
  function toggleCard(k) {
    setView('all'); setDetailItem(null)
    if (k === 'overdue' || k === 'today') setDateFilter(d => d === k ? '' : k)
    else if (k === 'high') setFilterHigh(v => !v)
    else setFilters(f => ({ ...f, type: defectsOn ? '' : 'Defect', status: 'open' }))
  }
  const clearAllFilters = () => { setFilters({ ...EMPTY_FILTERS }); setFilterHigh(false); setFilterAssignee(''); setDateFilter('') }
  const listView = view !== 'history' && view !== 'plan' && view !== 'calendar'
  const canWrite = !can || can('todo', 'write')
  const pageTitle = viewTitle || (view === 'plan' ? 'GA Plan' : view === 'calendar' ? 'Calendar' : '')
  const dateLine = new Date(today + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
  const deckAll = activeDeck ? allItems.filter(i => i.deck_id === activeDeck.id && i.pin_x != null) : []
  const pageSub = view === 'plan'
    ? [deckAll.filter(i => !i.done).length + ' open', deckAll.filter(i => i.done).length + ' closed', activeDeck?.name].filter(Boolean).join(' \u00b7 ')
    : listView
      ? shownItems.filter(i => !i.done).length + ' open' + (shownItems.some(i => i.done) ? ' \u00b7 ' + shownItems.filter(i => i.done).length + ' closed' : '')
      : ''
  const groups = sortNow === 'manual' ? null : groupByDate(shownItems, today)
  const WEEK_SUB = { today: new Date(today + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) }
  const dateChip = dateFilter ? [{ key: 'date', label: 'Due', value: dateFilter === 'overdue' ? 'Overdue' : 'Today', onRemove: () => setDateFilter('') }] : []
  const pageChips = [...dateChip, ...filterChips(viewFilters, setFilter, { options: filterOptions, showList: !activeListId && !activeFolderId }),
    ...(filterAssignee ? [{ key: 'assignee', label: 'Assignee', value: filterAssignee, onRemove: () => setFilterAssignee('') }] : []),
    ...(filterHigh ? [{ key: 'high', label: 'Priority', value: 'High only', onRemove: () => setFilterHigh(false) }] : [])]
  const filtersOn = filtersActive(filters) || filterHigh || !!filterAssignee || !!dateFilter

  // keys: N = new task, / = this page's search (the header search stays a click away), Esc = close the drawer
  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { if (lightbox) setLightbox(null); else if (detailItem) setDetailItem(null); return }
      if (detailItem && view !== 'plan') return
      if (isSearchShortcut(e)) {
        const box = pageRef.current && pageRef.current.querySelector('input[aria-label="Search"]')
        if (box) { e.preventDefault(); e.stopPropagation(); box.focus() }
        return
      }
      if ((e.key === 'n' || e.key === 'N') && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target) && addRef.current) { e.preventDefault(); addRef.current.focus() }
    }
    document.addEventListener('keydown', h, true)   // before the header's "/" (a listener on window)
    return () => document.removeEventListener('keydown', h, true)
  }, [detailItem, lightbox, view])
  // the Lists dropdown: closes on a click outside; a dragged task opens it (drop on a list), and it closes again when the drag ends
  useEffect(() => {
    if (!listsOpen) return
    const out = e => { if (listsRef.current && !listsRef.current.contains(e.target)) setListsOpen(false) }
    document.addEventListener('mousedown', out)
    return () => document.removeEventListener('mousedown', out)
  }, [listsOpen])
  useEffect(() => {
    const end = () => { if (listsByDrag.current) { listsByDrag.current = false; setListsOpen(false) } }
    document.addEventListener('dragend', end)
    return () => document.removeEventListener('dragend', end)
  }, [])
  // the drawer sits inside the content area (under the header, beside the menu)
  const drawerOpen = !!detailItem
  useEffect(() => {
    if (!drawerOpen) return
    const read = () => { const c = pageRef.current && pageRef.current.closest('.hc-content'); if (c) { const r = c.getBoundingClientRect(); setDrawerBox({ top: r.top, left: r.left }) } }
    read(); window.addEventListener('resize', read)
    return () => window.removeEventListener('resize', read)
  }, [drawerOpen])
  // the title grows with its text
  useEffect(() => { const t = titleRef.current; if (t) { t.style.height = 'auto'; t.style.height = t.scrollHeight + 'px' } }, [detailItem?.id, detailItem?.text])

  return (
    <div className={'td-page tdc' + (view === 'plan' ? ' plan' : '')} ref={pageRef}>
      {/* the views as big tabs, My Lists behind the Lists button */}
      {/* My Tasks only shown to crew sub-accounts */}
      <div className="tdc-tabrow">
        <div className="tdc-tabs" role="tablist" aria-label="To Do views">
          {[...(config?.accountType === 'crew' ? [['mine', 'My Tasks', counts.mine]] : []), ['today', 'Today', counts.today], ['next7', 'Next 7 Days', counts.next7], ['all', 'All Tasks', counts.all], ['calendar', 'Calendar', 0], ['plan', 'GA Plan', counts.pins], ['history', 'History', counts.completed]].map(([v, label, n]) => (
            <button key={v} type="button" role="tab" aria-selected={view === v} className={'tdc-tab' + (view === v ? ' on' : '')} onClick={() => goView(v)}>
              <span>{label}</span>{n > 0 && <span className="tdc-tab-n">{n}</span>}
            </button>
          ))}
        </div>
        <div className="tdc-lists-wrap" ref={listsRef}>
          <button type="button" className={'tdc-lists-btn' + (listsOpen ? ' open' : '') + (activeListId || activeFolderId ? ' on' : '')} aria-haspopup="dialog" aria-expanded={listsOpen} onClick={() => { listsByDrag.current = false; setListsOpen(v => !v) }}
            onDragEnter={() => { if (!listsOpen) { listsByDrag.current = true; setListsOpen(true) } }} onDragOver={e => e.preventDefault()}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            Lists
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          {listsOpen && (
      <div className="tdc-lists-pop" role="dialog" aria-label="Lists">
        <div style={s.viewScroll}>
          <div style={{ ...s.section, ...(dragOverList === 'unfile' ? s.dropTarget : {}) }} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverList !== 'unfile') setDragOverList('unfile') }} onDragLeave={() => setDragOverList(c => c === 'unfile' ? null : c)} onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('list-id'); setDragOverList(null); if (id && lists.some(l => l.id === id)) moveListToFolder(id, null) }}>My Lists (drop here to unfile)</div>
          {(() => {
            const renderListRow = (l, nested) => (
            <div key={l.id} className="td-nav" draggable onDragStart={(e) => { e.dataTransfer.setData('list-id', l.id); e.dataTransfer.effectAllowed = 'move' }} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverList !== l.id) setDragOverList(l.id) }} onDragLeave={() => setDragOverList(c => c === l.id ? null : c)} onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/plain'); if (id) moveToList(id, l.id) }} style={{ ...s.viewRow, ...(view === 'list:' + l.id ? s.viewRowActive : {}), ...(dragOverList === l.id ? s.dropTarget : {}) }} onClick={() => goView('list:' + l.id)}>
              <span style={s.listName}>{l.name}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {listCount(l.id) > 0 && <span style={s.badge}>{listCount(l.id)}</span>}
                {(config?.accountType !== 'crew' || l.created_by === (config?.crewName || config?.email)) && (
                  <div style={{ position: 'relative' }}>
                    <button title="Share with crew" className="td-icon" style={{ ...s.listDel, color: (l.shared_with||[]).length > 0 ? 'var(--blue)' : 'var(--muted)', fontSize: 14 }} onClick={(e) => { e.stopPropagation(); setSharePopover(sharePopover === l.id ? null : l.id) }}>⚑</button>
                    {sharePopover === l.id && (
                      <div style={{ position:'absolute', right:0, top:'100%', zIndex:200, background:'var(--card)', border:'1px solid var(--line-2)', borderRadius:10, padding:10, minWidth:180, boxShadow:'none' }} onClick={e => e.stopPropagation()}>
                        <div style={{ fontSize:11, color:'var(--muted)', marginBottom:6, fontWeight:600 }}>SHARE WITH</div>
                        {['chief_officer','engineer','steward','crew'].map(role => (
                          <label key={role} style={{ display:'flex', alignItems:'center', gap:6, padding:'3px 0', cursor:'pointer', fontSize:12, color:'var(--text)' }}>
                            <input type="checkbox" checked={isSharedWith(l, role)} onChange={e => { const sw = l.shared_with||[]; const next = e.target.checked ? [...sw,role] : sw.filter(x=>x!==role); saveShare(l.id, next) }} />
                            {role.replace('_',' ')}
                          </label>
                        ))}
                        {crew.length > 0 && <div style={{ fontSize:11, color:'var(--muted)', margin:'6px 0 4px', fontWeight:600 }}>SPECIFIC CREW</div>}
                        {crew.map(name => (
                          <label key={name} style={{ display:'flex', alignItems:'center', gap:6, padding:'3px 0', cursor:'pointer', fontSize:12, color:'var(--text)' }}>
                            <input type="checkbox" checked={isSharedWith(l, name)} onChange={e => { const sw = l.shared_with||[]; const next = e.target.checked ? [...sw,name] : sw.filter(x=>x!==name); saveShare(l.id, next) }} />
                            {name}
                          </label>
                        ))}
                        <button style={{ marginTop:8, fontSize:11, color:'var(--muted)', background:'none', border:'none', cursor:'pointer', padding:0 }} onClick={() => setSharePopover(null)}>Close</button>
                      </div>
                    )}
                  </div>
                )}
                <button className="td-icon danger" style={s.listDel} onClick={(e) => { e.stopPropagation(); deleteList(l.id) }}>×</button>
              </span>
            </div>
            )
            const standalone = lists.filter(l => !l.folder_id || !folders.some(f => f.id === l.folder_id))
            const folderCount = (fid) => lists.filter(l => l.folder_id === fid).reduce((n, l) => n + listCount(l.id), 0)
            return (
              <>
                {folders.map(f => {
                  const isOpen = !collapsed.has(f.id)
                  const kids = lists.filter(l => l.folder_id === f.id)
                  return (
                    <div key={f.id}>
                      <div className="td-nav" style={{ ...s.viewRow, fontWeight: 600, ...(view === 'folder:'+f.id ? s.viewRowActive : {}), ...(dragOverList === ('folder:'+f.id) ? s.dropTarget : {}) }} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect='move'; if (dragOverList !== 'folder:'+f.id) setDragOverList('folder:'+f.id) }} onDragLeave={() => setDragOverList(c => c === 'folder:'+f.id ? null : c)} onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('list-id'); setDragOverList(null); if (id && lists.some(l => l.id === id)) moveListToFolder(id, f.id) }} onClick={() => goView('folder:' + f.id)}>
                        <span style={s.listName}><span onClick={(e) => { e.stopPropagation(); setCollapsed(c => { const n = new Set(c); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n }) }} style={{ cursor: 'pointer', marginRight: 2 }}>{isOpen ? '▾' : '▸'}</span> <span onDoubleClick={(e) => { e.stopPropagation(); const nm = prompt('Folder name', f.name); if (nm && nm.trim()) renameFolder(f.id, nm.trim()) }}>{f.name}</span></span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {folderCount(f.id) > 0 && <span style={s.badge}>{folderCount(f.id)}</span>}
                          <button className="td-icon danger" style={s.listDel} onClick={(e) => { e.stopPropagation(); deleteFolder(f.id) }}>×</button>
                        </span>
                      </div>
                      {isOpen && <div style={{ paddingLeft: 12 }}>{kids.map(l => renderListRow(l, true))}{!kids.length && <div style={{ ...s.emptyHint, padding: '6px 4px', fontSize: 12 }}>Empty</div>}</div>}
                    </div>
                  )
                })}
                {standalone.map(l => renderListRow(l, false))}
                {!lists.length && !folders.length && !loading && <div style={s.emptyHint}>No lists yet.</div>}
              </>
            )
          })()}
        </div>
        <div style={s.newListRow}>
          <input style={s.newListInput} placeholder="New list / worklist…" value={newListName} onChange={e => setNewListName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addList()} />
          {(!can || can('todo','write')) && <button className="td-soft" style={s.addListBtn} onClick={addList} disabled={busy || !newListName.trim()}>+</button>}
        </div>
        <div style={s.newListRow}>
          <input style={s.newListInput} placeholder="New folder…" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addFolder()} />
          {(!can || can('todo','write')) && <button className="td-soft" style={s.addListBtn} onClick={addFolder} disabled={!newFolderName.trim()}>+</button>}
        </div>
      </div>

          )}
        </div>
      </div>

      <div className="tdc-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="tdc-title">{pageTitle}</h1>
          <div className="tdc-sub">{pageSub}{view !== 'plan' && <span className="tdc-sub-date">{pageSub ? ' \u00b7 ' : ''}{dateLine}</span>}</div>
        </div>
        {listView && (
          <div className="tdc-actions">
            {canWrite && <button className={'td-btn tdc-act' + (selecting ? ' on' : '')} onClick={() => { setSelecting(v => !v); setSelected(new Set()) }}>{selecting ? 'Cancel' : 'Select'}</button>}
            {shownItems.some(i => i.deck_id && i.pin_x != null) && <button className="td-btn tdc-act" onClick={viewPlan} title="Open the GA plan with only these items' pins">View plan</button>}
            <span style={{ position: 'relative' }}>
              <button className="td-btn tdc-act" onClick={() => setExportOpen(v => !v)} disabled={!shownItems.length}>Export ▾</button>
              {exportOpen && (
                <span style={{ ...s.menu, right: 0 }} onMouseLeave={() => setExportOpen(false)}>
                  <button className="td-menu-item" style={s.menuBtn} onClick={() => exportList('xlsx')}>Excel (.xlsx)</button>
                  <button className="td-menu-item" style={s.menuBtn} onClick={() => exportList('csv')}>CSV</button>
                  <button className="td-menu-item" style={s.menuBtn} onClick={() => exportList('pdf')}>PDF (print for the yard)</button>
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Task list / calendar */}
      <div className="tdc-main">
        {view === 'history' ? (
          <TodoHistory who={who} today={todayStr()} items={allItems} filters={filters} setFilter={setFilter} names={exportNames}
            clearFilters={() => setFilters({ ...EMPTY_FILTERS })} canWrite={!can || can('todo', 'write')}
            onOpen={setDetailItem} onReopen={item => toggleItem(item)} onExport={exportHistory}
            options={filterOptions} />
        ) : view === 'plan' ? (
          <PlanView decks={decks} activeDeck={activeDeck} setActiveDeck={setActiveDeck}
            allItems={allItems} onPick={setDetailItem} detailItem={detailItem}
            pinMode={pinMode} setPinMode={setPinMode} createPin={createPin} movePin={movePin}
            placingItem={placingItem} setPlacingItem={setPlacingItem} placePin={placePin}
            zones={zones} zoneMode={zoneMode} setZoneMode={setZoneMode}
            createZone={createZone} updateZone={updateZone} deleteZone={deleteZone}
            addDecks={addDecks} renameDeck={renameDeck} deleteDeck={deleteDeck}
            deckUploading={deckUploading} deckFileRef={deckFileRef}
            exportSnagPdf={exportSnagPdf} exporting={exporting} planIds={planIds} clearPlanIds={() => setPlanIds(null)}
            cropMode={cropMode} setCropMode={setCropMode} cropDeck={cropDeck}
            lsaItems={lsaItems} showLsa={showLsa} setShowLsa={setShowLsa}
            autoPlaceLsa={autoPlaceLsa} mapping={mapping}
            moveLsa={moveLsa} placingLsa={placingLsa} setPlacingLsa={setPlacingLsa} placeLsa={placeLsa} />
        ) : view === 'calendar' ? (
          <CalendarView allItems={allItems} calMonth={calMonth} setCalMonth={setCalMonth} onPick={setDetailItem} listName={listName} />
        ) : (
        <>
        <div className="tdc-cards">
          {[['overdue', 'Overdue', summary.overdue], ['today', 'Due today', summary.today], ['defects', 'Open defects', summary.defects], ['high', 'High priority', summary.high]].map(([k, label, n]) => (
            <button key={k} type="button" aria-pressed={cardOn[k]} className={'tdc-card' + (k === 'overdue' && n > 0 ? ' red' : '') + (cardOn[k] ? ' on' : '')} onClick={() => toggleCard(k)}>
              <span style={{ minWidth: 0 }}>
                <span className="tdc-card-n">{itemsReady ? n : <span className="tdc-sk" style={{ width: 26, height: 26 }} />}</span>
                <span className="tdc-card-l">{label}</span>
              </span>
              <span className="tdc-card-i"><svg width="19" height="19" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{CARD_ICON[k]}</svg></span>
            </button>
          ))}
        </div>

        <div className="tdc-toolbar">
          {/* add box above the list (desktop and phone: the same component; it was lost in the filter redesign - audit-full F-3) */}
          {canWrite && (
            <div className="tdc-add">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3v10M3 8h10" stroke="var(--blue)" strokeWidth="1.9" strokeLinecap="round" /></svg>
              <input ref={addRef} aria-label="Add a task" placeholder="Add a task…  try: fuel check Friday !high @name" value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && !busy && addItem()} />
              <span className="tdc-kbd">N</span>
              <button className="td-primary" style={s.addBtn} onClick={addItem} disabled={busy || !newItem.trim()}>{busy ? '…' : 'Add'}</button>
            </div>
          )}
          <TodoFilterBar filters={viewFilters} setFilter={setFilter} typeTabs={typeTabs} showList={!activeListId && !activeFolderId}
            anyOn={filtersOn} clearAll={clearAllFilters} options={filterOptions} resultCount={shownItems.length} keyHint="/"
            extraCount={(filterAssignee ? 1 : 0) + (filterHigh ? 1 : 0) + (dateFilter ? 1 : 0)}
            extra={<>
              <div><div className="tdf-cap">Assignee</div><PillSelect label="Anyone" value={filterAssignee} onChange={v => setFilterAssignee(v)} options={crew.map(name => ({ value: name, label: name }))} /></div>
              <div><div className="tdf-cap">Priority</div><span className={'tdf-pill tdf-toggle' + (filterHigh ? ' on' : '')}><button type="button" className="tdf-pill-main" aria-pressed={filterHigh} onClick={() => setFilterHigh(v => !v)}><Flag color={PRIORITY[3].color} size={12} /> High only</button></span></div>
            </>}
            sort={<PillSelect prefix="Sort:" label="Sort" clearable={false} align="right" value={sortNow} onChange={v => setSortBy(v)}
              options={[{ value: 'priority', label: 'Priority' }, { value: 'due', label: 'Due date' }, ...(activeListId ? [{ value: 'manual', label: 'Manual' }] : [])]} />} />
        </div>
        <TodoFilterChips chips={pageChips} clearAll={clearAllFilters} />

        {selecting && (
          <div style={s.bulkBar}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!shownItems.length && shownItems.every(i => selected.has(i.id))} onChange={e => setSelected(e.target.checked ? new Set(shownItems.map(i => i.id)) : new Set())} />
              {selected.size} selected
            </label>
            <div style={{ flex: 1 }} />
            <button className="td-primary" style={s.primaryChip} disabled={!selected.size} onClick={() => setBulk({ status: '', assignee: '', list_id: '', due_date: '', type: '' })}>Update tasks</button>
          </div>
        )}
        {bulk && (
          <div style={s.bulkPanel}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Update {selected.size} task{selected.size === 1 ? '' : 's'} <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11.5 }}>· empty = leave as it is</span></div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="tdf" style={{ display: 'contents' }}>
                <PillSelect label="Status" value={bulk.status} onChange={v => setBulk(b => ({ ...b, status: v }))} options={[{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }]} />
                <PillSelect label="Assignee" value={bulk.assignee} onChange={v => setBulk(b => ({ ...b, assignee: v }))} options={[{ value: '-', label: 'Unassigned' }, ...crew.map(n => ({ value: n, label: n }))]} />
                <PillSelect label="Move to list" value={bulk.list_id} onChange={v => setBulk(b => ({ ...b, list_id: v }))} options={lists.map(l => ({ value: l.id, label: l.name }))} />
                <PillSelect label="Type" value={bulk.type} onChange={v => setBulk(b => ({ ...b, type: v }))} options={TODO_TYPES.map(t => ({ value: t, label: t }))} />
              </span>
              <span style={{ width: 150 }}><DateField value={bulk.due_date === '-' ? '' : bulk.due_date} onChange={v => setBulk(b => ({ ...b, due_date: v || '-' }))} style={s.fieldInput} /></span>
              <div style={{ flex: 1 }} />
              <button className="td-btn" style={s.filterChip} onClick={() => setBulk(null)}>Cancel</button>
              <button className="td-primary" style={s.primaryChip} disabled={busy || !Object.keys(bulkPatch(bulk)).length} onClick={applyBulk}>{busy ? 'Saving…' : 'Apply'}</button>
            </div>
          </div>
        )}

        <div style={s.items}>
          {(() => {
          const renderTaskRow = (item) => {
            const overdue = isOverdue(item.due_date, item.done)
            const pr = PRIORITY[item.priority || 0]
            const anim = completing === item.id
            return (
              <div key={item.id} className={'td-item' + (detailItem?.id === item.id ? ' on' : '') + (item.pending ? ' pending' : '')} data-todo-id={item.id} draggable={!item.pending} onDragStart={(e) => { e.dataTransfer.setData('text/plain', item.id); e.dataTransfer.effectAllowed = 'move' }} onDragOver={activeListId ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverItem !== item.id) setDragOverItem(item.id) } : undefined} onDragLeave={() => setDragOverItem(c => c === item.id ? null : c)} onDrop={activeListId ? (e) => { e.preventDefault(); e.stopPropagation(); const id = e.dataTransfer.getData('text/plain'); setDragOverItem(null); if (id) reorderWithin(id, item.id) } : undefined} style={{ ...(dragOverItem === item.id ? { borderTop: '2px solid var(--blue)' } : {}), opacity: anim ? 0 : undefined, transform: anim ? 'translateX(8px)' : undefined }} onClick={() => { if (!item.pending) setDetailItem(item) }}>
                {selecting && <input type="checkbox" aria-label={'Select ' + item.text} checked={selected.has(item.id)} onClick={e => e.stopPropagation()} onChange={() => toggleSel(item.id)} style={{ accentColor: 'var(--blue)', cursor: 'pointer', flexShrink: 0 }} />}
                <span className="td-flag" onClick={(e) => { e.stopPropagation(); cyclePriority(item) }} title={'Priority: ' + pr.label + ' · click to change'}><Flag color={pr.color} /></span>
                <button className={'td-check' + (item.done ? ' done' : '')} aria-label={item.done ? 'Reopen task' : 'Close task'} onClick={(e) => { e.stopPropagation(); toggleWithAnim(item) }}>{item.done ? '✓' : ''}</button>
                {/* title, type badge, then every other tag: a tag that does not fit drops out whole (.td-* in global.css) */}
                <span className="td-row-chips">
                  <span className="td-title" title={item.text} style={item.done ? s.itemTextDone : undefined}>{item.text}</span>
                  <span className={'td-badge ' + typeOf(item).toLowerCase()}>{typeOf(item)}</span>
                  {item.department && <span className="td-chip">{item.department}</span>}
                  {(item.deck_id || item.location_text) && <span className="td-chip loc" title="Location">{item.deck_id ? '⚑ ' + (zoneName(item.zone_id) || decks.find(d => d.id === item.deck_id)?.name || 'Plan') : item.location_text}</span>}
                  {(view === 'today' || view === 'next7' || view === 'all') && item.list_id && <span className="td-chip" title={listName(item.list_id)}>{listName(item.list_id)}</span>}
                  {item.equipment_id && <span className="td-chip" title={'Component: ' + equipmentName(item.equipment_id)}>⚙ {equipmentName(item.equipment_id)}</span>}
                  {item.responsible_text && <span className="td-chip" title={item.responsible_text}>{item.responsible_text}</span>}
                  {item.recurrence && <span className="td-chip" title="Repeats">↻</span>}
                </span>
                {viewFilters.status !== 'open' && <span style={{ ...s.statusPill, ...(item.done ? s.statusClosed : s.statusOpen) }}>{statusOf(item)}</span>}
                <span className="td-due-wrap">
                  <span className={'td-due' + (item.due_date ? '' : ' none') + (overdue ? ' late' : '')} onClick={(e) => { e.stopPropagation(); setDueMenu(m => m === item.id ? null : item.id) }} title="Set due date">{item.due_date ? fmtDate(item.due_date) : '+ date'}</span>
                  {dueMenu === item.id && (
                    <span onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '120%', right: 0, zIndex: 50, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 10, padding: 5, display: 'flex', flexDirection: 'column', minWidth: 130 }}>
                      {[['Today', relDateISO(0)], ['Tomorrow', relDateISO(1)], ['Next week', relDateISO(7)]].map(([lbl, iso]) => (
                        <button key={lbl} className="td-menu-item" onClick={() => setDue(item, iso)} style={s.menuBtn}>{lbl}</button>
                      ))}
                      {item.due_date && <button className="td-menu-item" onClick={() => setDue(item, null)} style={{ ...s.menuBtn, color: '#C93B47' }}>Clear</button>}
                    </span>
                  )}
                </span>
                {item.assignee ? <span className="td-av" title={item.assignee} aria-label={'Assigned to ' + item.assignee} style={{ background: avatarColor(item.assignee) }}>{initialsOf(item.assignee)}</span> : <span className="td-av none" title="Unassigned" />}
                <button className="td-icon danger td-del" aria-label={'Delete ' + item.text} onClick={(e) => { e.stopPropagation(); deleteItem(item.id) }}>×</button>
              </div>
            )
          }
          if (!itemsReady) return <div className="tdc-skel" aria-hidden="true">{[58, 36, 48, 30, 42].map((w, i) => <div key={i} className="tdc-skel-row"><span className="tdc-sk" style={{ width: 14, height: 14 }} /><span className="tdc-sk" style={{ width: 20, height: 20, borderRadius: '50%' }} /><span className="tdc-sk" style={{ width: w + '%', height: 13 }} /><span style={{ flex: 1 }} /><span className="tdc-sk" style={{ width: 44, height: 13 }} /><span className="tdc-sk" style={{ width: 28, height: 28, borderRadius: '50%' }} /></div>)}</div>
          if (activeFolderId) {
            const fLists = lists.filter(l => l.folder_id === activeFolderId)
            if (!fLists.length) return <div style={s.emptyHint}>This folder has no lists yet.</div>
            return fLists.map(fl => {
              const listItems = applyFilters(allItems.filter(i => !i.done && i.list_id === fl.id), viewFilters, exportNames)
              const secOpen = !collapsed.has('sec:' + fl.id)
              return (
                <div key={fl.id} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 2px', cursor: 'pointer', color: 'var(--text)', fontWeight: 600, fontSize: 13 }} onClick={() => setCollapsed(c => { const n = new Set(c); const k = 'sec:' + fl.id; n.has(k) ? n.delete(k) : n.add(k); return n })}>
                    <span>{secOpen ? '▾' : '▸'}</span>
                    <span>{fl.name}</span>
                    {listItems.length > 0 && <span style={s.badge}>{listItems.length}</span>}
                  </div>
                  {secOpen && (listItems.length ? listItems.map(renderTaskRow) : <div style={{ ...s.emptyHint, padding: '4px 4px 8px 20px', fontSize: 12, textAlign: 'left' }}>No tasks.</div>)}
                </div>
              )
            })
          }
          return (<>
            {groups ? groups.map(g => (
              <section key={g.key} className={'tdc-group ' + g.key} aria-label={g.label}>
                <div className="tdc-group-h"><b>{g.label}</b><span className="n">{g.items.length}</span>{WEEK_SUB[g.key] && <span>{WEEK_SUB[g.key]}</span>}</div>
                {g.items.map(renderTaskRow)}
              </section>
            )) : shownItems.map(renderTaskRow)}
            {!shownItems.length && (
              <div className="tdc-empty">
                <span className="tdc-empty-i"><svg width="24" height="24" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.3l2.4 2.4 4.6-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                <div className="tdc-empty-t">{filtersOn ? 'Nothing matches these filters' : view === 'today' ? 'Nothing due today' : view === 'next7' ? 'Nothing due in the next 7 days' : 'All clear'}</div>
                <div className="tdc-empty-s">{(!can || can('todo','write')) ? 'No tasks here. Add one above.' : 'No tasks here.'}</div>
                {filtersOn && <button type="button" className="tdf-clear" style={{ marginTop: 8 }} onClick={clearAllFilters}>Clear all</button>}
              </div>
            )}
          </>)
          })()}
        </div>
        </>
        )}
      </div>

      {printMode && createPortal(
        <div id="hc-snag-print" style={{ position:'absolute', left:0, top:0, width:'1000px', height:'auto', boxSizing:'border-box', background:'#fff', zIndex:9999, padding:'34px 38px', fontFamily:'Inter, sans-serif', color:'#16233B', overflow:'visible' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', paddingBottom:10 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, letterSpacing:1.5, color:'#1B4FD8' }}>HEYCAPTAIN</div>
              <div style={{ fontSize:10, color:'#67748C', letterSpacing:2, textTransform:'uppercase' }}>Snag List</div>
            </div>
            <div style={{ textAlign:'right', fontSize:10, color:'#67748C', lineHeight:1.7 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#16233B' }}>{config?.vessel_name || 'Vessel'}</div>
              <div>{fmtLong(todayLocal())}</div>
            </div>
          </div>
          <div style={{ background:'#1B4FD8', color:'#fff', padding:'7px 12px', fontSize:11, letterSpacing:2, textTransform:'uppercase', marginBottom:18 }}>
            {allItems.filter(i => i.deck_id && !i.done).length} open · {allItems.filter(i => i.deck_id && i.done).length} closed
          </div>

          {decks.map(dk => {
            const dp = allItems.filter(i => i.deck_id === dk.id && i.pin_x != null)
            if (!dp.length) return null
            return (
              <div key={dk.id} style={{ marginBottom:26, pageBreakInside:'avoid' }}>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:8, color:'#1B4FD8' }}>{dk.name}</div>
                <div style={{ position:'relative', width:'100%', border:'1px solid #E5E9F1', borderRadius:6, overflow:'hidden' }}>
                  <FileImg src={dk.image_url} alt="" crossOrigin="anonymous" style={{ width:'100%', display:'block' }} />
                  {dp.map((p, i) => (
                    <div key={p.id} style={{
                      position:'absolute', left:(p.pin_x*100)+'%', top:(p.pin_y*100)+'%', transform:'translate(-50%,-50%)',
                      width:18, height:18, borderRadius:'50%', border:'2px solid #fff',
                      background: pinColor(p),
                      color:'#fff', fontSize:10, fontWeight:700, lineHeight:'14px', textAlign:'center',
                    }}>{i+1}</div>
                  ))}
                </div>
                <table style={{ width:'100%', borderCollapse:'collapse', marginTop:10, fontSize:10 }}>
                  <thead>
                    <tr style={{ background:'#F3F5F9', color:'#67748C', textAlign:'left' }}>
                      <th style={{ padding:'5px 7px', width:26 }}>#</th>
                      <th style={{ padding:'5px 7px' }}>Item</th>
                      <th style={{ padding:'5px 7px', width:80 }}>Zone</th>
                      <th style={{ padding:'5px 7px', width:60 }}>Priority</th>
                      <th style={{ padding:'5px 7px', width:80 }}>Assigned</th>
                      <th style={{ padding:'5px 7px', width:50 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dp.map((p, i) => (
                      <tr key={p.id} style={{ borderBottom:'1px solid #E5E9F1' }}>
                        <td style={{ padding:'5px 7px', fontWeight:700 }}>{i+1}</td>
                        <td style={{ padding:'5px 7px' }}>{p.text}</td>
                        <td style={{ padding:'5px 7px', color:'#67748C' }}>{zones.find(z => z.id === p.zone_id)?.name || '—'}</td>
                        <td style={{ padding:'5px 7px' }}>{PRIORITY[p.priority||0]?.label || '—'}</td>
                        <td style={{ padding:'5px 7px', color:'#67748C' }}>{p.assignee || '—'}</td>
                        <td style={{ padding:'5px 7px', color: p.done ? '#137050' : '#C93B47', fontWeight:600 }}>{p.done ? 'Closed' : 'Open'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          })}
          <div style={{ borderTop:'1px solid #E5E9F1', paddingTop:8, marginTop:10, fontSize:9, color:'#5b6878', display:'flex', justifyContent:'space-between' }}>
            <span>{config?.vessel_name || 'Vessel'} · Snag List</span>
            <span>Generated {fmtLong(todayLocal())} · HEYCAPTAIN</span>
          </div>
        </div>,
        document.body
      )}

      {mapResult && (
        <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(10,22,40,.55)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setMapResult(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--card)', borderRadius:14, width:640, maxWidth:'94%', maxHeight:'82vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--line)' }}>
              <div style={{ fontSize:15, fontWeight:700, color:'var(--text)' }}>Review LSA placement</div>
              <div style={{ fontSize:12, color:'var(--muted)', marginTop:3 }}>
                {mapResult.filter(r => r.zone).length} of {mapResult.length} locations matched.
                Uncheck anything wrong &mdash; nothing is saved until you apply.
              </div>
            </div>
            <div style={{ overflowY:'auto', padding:'6px 20px' }}>
              {mapResult.map((r, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid var(--line)' }}>
                  <input type="checkbox" checked={r.accept} disabled={!r.zone}
                    onChange={e => setMapResult(m => m.map((x, j) => j === i ? { ...x, accept: e.target.checked } : x))}
                    style={{ accentColor:'var(--blue)', cursor: r.zone ? 'pointer' : 'not-allowed' }} />
                  <span style={{ fontSize:12.5, color:'var(--text)', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.location}</span>
                  <span style={{ fontSize:11, color:'var(--muted)' }}>{r.count}&times;</span>
                  <span style={{ fontSize:12, fontWeight:600, minWidth:120, textAlign:'right', color: r.zone ? '#137050' : 'var(--muted)' }}>
                    {r.zone ? r.zone.name : 'no match'}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ padding:'14px 20px', borderTop:'1px solid var(--line)', display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:12, color:'var(--muted)' }}>
                {mapResult.filter(r => r.accept && r.zone).reduce((n, r) => n + r.count, 0)} items will be pinned
              </span>
              <div style={{ flex:1 }} />
              <button className="td-btn" onClick={() => setMapResult(null)} style={{ padding:'8px 14px', borderRadius:9, fontSize:12, cursor:'pointer', border:'1px solid var(--line)', background:'transparent', color:'var(--muted)' }}>Cancel</button>
              <button className="td-primary" onClick={applyMapping} style={{ padding:'8px 16px', borderRadius:9, fontSize:12, fontWeight:600, cursor:'pointer', border:'none', background:'var(--blue)', color:'#fff' }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* the task drawer: over the list, a click outside closes it. On the GA plan nothing is dimmed: the drawing stays in view and another pin switches the task */}
      {detailItem && createPortal(<>
        {view !== 'plan' && <div className="tdc-scrim" style={drawerBox || undefined} onClick={() => setDetailItem(null)} />}
        <aside className={'td-page tdc-drawer' + (view === 'plan' ? ' on-plan' : '')} role="dialog" aria-label="Task" style={drawerBox ? { top: drawerBox.top } : undefined}>
          <div style={s.detailHead}>
            <span style={{ ...s.statusPill, ...(detailItem.done ? s.statusClosed : s.statusOpen) }}>{statusOf(detailItem)}</span>
            <div style={{ flex: 1 }} />
            <span style={{ marginRight: 12 }}><HistoryLink table="todo_items" rowId={detailItem.id} label={detailItem.text} style={{ color: 'var(--blue)' }} /></span>
            <button className="td-icon" style={s.detailClose} aria-label="Close" onClick={() => setDetailItem(null)}>×</button>
          </div>
          <div className="tdc-dbody">
          <textarea ref={titleRef} className="tdc-dtitle" rows={1} aria-label="Task title" value={detailItem.text} onChange={e => updateItem(detailItem.id, { text: e.target.value.replace(/\n/g, ' ') })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }} />
          <div style={{ fontSize: 12.5, color: 'var(--muted)', margin: '0 0 14px' }}>
            {detailItem.created_at && <span>Created {fmtLong(dayOf(detailItem.created_at))}{detailItem.created_by ? ' by ' + detailItem.created_by : ''}</span>}
            {detailItem.done && detailItem.closed_at && <span> · Closed {fmtLong(dayOf(detailItem.closed_at))}{detailItem.closed_by ? ' by ' + detailItem.closed_by : ''}</span>}
          </div>
          <div className="tdc-g2">
            <div style={s.field}>
              <label style={s.fieldLabel}>Type</label>
              <select style={s.fieldInput} value={typeOf(detailItem)} onChange={e => updateItem(detailItem.id, { type: e.target.value })}>
                {TODO_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Department</label>
              <select style={s.fieldInput} value={detailItem.department || ''} onChange={e => updateItem(detailItem.id, { department: e.target.value || null })}>
                <option value="">None</option>
                {[...new Set([...DEPARTMENTS, ...(detailItem.department ? [detailItem.department] : [])])].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Component</label>
              <select style={s.fieldInput} value={detailItem.equipment_id || ''} onChange={e => updateItem(detailItem.id, { equipment_id: e.target.value || null })}>
                <option value="">None</option>
                {detailItem.equipment_id && !equipment.some(e => e.id === detailItem.equipment_id) && <option value={detailItem.equipment_id}>{equipmentName(detailItem.equipment_id) || 'Equipment'}</option>}
                {equipment.map(e => <option key={e.id} value={e.id}>{(e.name || e.uid || 'Equipment') + (e.uid && e.name ? ' (' + e.uid + ')' : '')}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Due date</label>
              <DateField value={detailItem.due_date || ''} onChange={v => updateItem(detailItem.id, { due_date: v || null })} style={isOverdue(detailItem.due_date, detailItem.done) ? { ...s.fieldInput, color: '#C93B47', fontWeight: 600 } : s.fieldInput} />
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Assignee</label>
              <Dropdown value={detailItem.assignee || ''} placeholder="Unassigned" onChange={v => updateItem(detailItem.id, { assignee: v || null })} options={[{ value: '', label: 'Unassigned' }, ...crew.map(name => ({ value: name, label: name }))]} />
            </div>
            <div style={s.field}>
              <label style={s.fieldLabel}>Repeat</label>
              <Dropdown value={detailItem.recurrence || ''} placeholder="No repeat" onChange={v => updateItem(detailItem.id, { recurrence: v || null })} options={[{ value: '', label: 'No repeat' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }]} />
            </div>
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Priority</label>
            <div style={s.prRow}>
              {[3, 2, 1, 0].map(p => (
                <button key={p} className="td-btn tdc-prio" aria-pressed={(detailItem.priority || 0) === p} style={(detailItem.priority || 0) === p ? { background: PRIORITY[p].color, color: p ? '#fff' : 'var(--text)', borderColor: PRIORITY[p].color } : undefined} onClick={() => updateItem(detailItem.id, { priority: p })}><Flag color={(detailItem.priority || 0) === p ? (p ? '#fff' : '#5E6B7F') : PRIORITY[p].color} size={12} />{PRIORITY[p].label}</button>
              ))}
            </div>
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Person responsible (not in the crew list)</label>
            <input style={s.fieldInput} list="hc-todo-roles" defaultValue={detailItem.responsible_text || ''} key={'r' + detailItem.id} placeholder="e.g. Shipyard, Contractor, Captain/Engineer"
              onBlur={e => { const v = e.target.value.trim() || null; if (v !== (detailItem.responsible_text || null)) updateItem(detailItem.id, { responsible_text: v }) }} />
            <datalist id="hc-todo-roles">{RESPONSIBLE_ROLES.map(r => <option key={r} value={r} />)}</datalist>
          </div>
          <div style={s.field}>
            <label style={s.fieldLabel}>Notes</label>
            <textarea style={s.fieldArea} value={detailItem.notes || ''} placeholder="Add detail…" onChange={e => updateItem(detailItem.id, { notes: e.target.value })} />
          </div>
          {detailItem.id && !String(detailItem.id).startsWith('new') && <RecordComments type="defect" id={detailItem.id} />}{/* Communication */}
          <div style={s.field}>
            <label style={s.fieldLabel}>Sub-tasks</label>
            {subTasks.map(st => (
              <div key={st.id} style={s.subRow}>
                <input type="checkbox" checked={st.done} onChange={e => toggleSubTask(st.id, e.target.checked)} style={{ accentColor: 'var(--blue)', cursor: 'pointer' }} />
                <span style={{ ...s.subText, textDecoration: st.done ? 'line-through' : 'none', opacity: st.done ? 0.5 : 1 }}>{st.text}</span>
                <button className="td-icon danger" style={s.subDel} onClick={() => deleteSubTask(st.id)}>×</button>
              </div>
            ))}
            <div style={s.subAdd}>
              <input style={s.subIn} value={subInput} onChange={e => setSubInput(e.target.value)} placeholder="Add sub-task…" onKeyDown={e => e.key === 'Enter' && addSubTask()} />
              <button className="td-primary" style={s.subBtn} onClick={addSubTask}>+</button>
            </div>
          </div>

          {!detailItem.deck_id && (
            <div style={s.field}>
              <label style={s.fieldLabel}>Location</label>
              <input style={{ ...s.fieldInput, marginBottom: decks.length ? 8 : 0 }} defaultValue={detailItem.location_text || ''} key={'l' + detailItem.id} placeholder="e.g. Engine room, Aft deck"
                onBlur={e => { const v = e.target.value.trim() || null; if (v !== (detailItem.location_text || null)) updateItem(detailItem.id, { location_text: v }) }} />
              {decks.length > 0 && <button className="td-btn" style={{ padding:'7px 12px', borderRadius:8, fontSize:12, cursor:'pointer', fontWeight:600,
                border:'1px solid var(--line-2)', background:'var(--card)', color:'var(--blue)' }}
                onClick={() => { setPlacingItem(detailItem); setView('plan') }}>⚑ Pin on plan</button>}
            </div>
          )}

          {detailItem.deck_id && (
            <div style={s.field}>
              <label style={s.fieldLabel}>Location</label>
              <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text)' }}>
                <span style={{ padding:'3px 9px', borderRadius:7, background:'rgba(27,79,216,.08)', border:'1px solid rgba(27,79,216,.2)' }}>
                  ⚑ {decks.find(d => d.id === detailItem.deck_id)?.name || 'Plan'}{zoneName(detailItem.zone_id) ? ' / ' + zoneName(detailItem.zone_id) : ''}
                </span>
                <button style={{ background:'none', border:'none', color:'var(--blue)', cursor:'pointer', fontSize:12, padding:0 }}
                  onClick={() => { const d = decks.find(x => x.id === detailItem.deck_id); if (d) { setActiveDeck(d); setView('plan') } }}>View on plan</button>
                <div style={{ flex:1 }} />
                <button style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:12, padding:0 }}
                  onClick={() => updateItem(detailItem.id, { deck_id: null, pin_x: null, pin_y: null })}>Remove pin</button>
              </div>
            </div>
          )}

          <div style={s.field}>
            <label style={s.fieldLabel}>Photos</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {(detailItem.photos || []).map(url => (
                <div key={url} style={{ position:'relative' }}>
                  <FileImg src={url} alt="" onClick={() => setLightbox(url)}
                    style={{ width:72, height:72, objectFit:'cover', borderRadius:8, border:'1px solid var(--line)', cursor:'zoom-in' }} />
                  <button onClick={() => removePhoto(url)}
                    style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', border:'1px solid var(--line)',
                      background:'var(--card)', color:'var(--muted)', cursor:'pointer', fontSize:12, lineHeight:'18px', padding:0 }}>×</button>
                </div>
              ))}
              <button className="td-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                style={{ width:72, height:72, borderRadius:10, border:'1px dashed var(--line-2)', background:'transparent',
                  color:'var(--muted)', cursor:'pointer', fontSize:11, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:3 }}>
                <span style={{ fontSize:18 }}>+</span>{uploading ? 'Uploading' : 'Photo'}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple style={{ display:'none' }}
              onChange={e => { uploadPhotos(Array.from(e.target.files || [])); e.target.value = '' }} />
          </div>

          <div style={s.field}>
            <label style={s.fieldLabel}>Activity</label>
            {comments.map(c => (
              <div key={c.id} style={{ display:'flex', gap:8, padding:'7px 0', borderBottom:'1px solid var(--line)' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, color:'var(--muted)', marginBottom:2 }}>
                    {c.author} · {new Date(c.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric'})} {new Date(c.created_at).toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'})}
                  </div>
                  <div style={{ fontSize:13, color:'var(--text)', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{c.body}</div>
                </div>
                <button className="td-icon danger" style={s.subDel} onClick={() => deleteComment(c.id)}>×</button>
              </div>
            ))}
            {!comments.length && <div style={{ fontSize:12, color:'var(--muted)', padding:'4px 0' }}>No updates yet.</div>}
            <div style={s.subAdd}>
              <input style={s.subIn} value={commentInput} onChange={e => setCommentInput(e.target.value)} placeholder="Add an update…"
                onKeyDown={e => { if (e.key === 'Enter') { addComment(commentInput); setCommentInput('') } }} />
              <button className="td-primary" style={s.subBtn} onClick={() => { addComment(commentInput); setCommentInput('') }}>+</button>
            </div>
          </div>

          </div>
          <div className="tdc-dfoot">
            <button className="td-danger" style={s.detailDel} onClick={() => deleteItem(detailItem.id)}>Delete task</button>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#137050', opacity: savedFlash ? 1 : 0, transition: 'opacity .25s' }}>Saved ✓</span>
          </div>

          {lightbox && (
            <div onClick={() => setLightbox(null)} style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(10,22,40,.86)',
              display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out' }}>
              <FileImg src={lightbox} alt="" style={{ maxWidth:'92%', maxHeight:'92%', borderRadius:10 }} />
            </div>
          )}
        </aside>
      </>, document.body)}
    </div>
  )
}

// the plain message when a deck's drawing cannot be shown
function planImageProblem(deck) {
  const why = fileProblem(deck.image_url)
  const reason = why === 'denied' ? 'This login is not allowed to open the file.'
    : why === 'not_found' ? 'The file is no longer in storage: add the plan again with + Plan.'
    : why === 'error' ? 'The server could not open it just now.'
    : fileSigned(deck.image_url) ? 'The file opened but is not an image the browser can show: add the plan again with + Plan.'
    : 'The server could not be reached.'
  return { title: 'The drawing for ' + deck.name + ' could not be loaded.', reason, code: why || (fileSigned(deck.image_url) ? 'image' : 'no answer') }
}

function PlanView({ decks, activeDeck, setActiveDeck, allItems, onPick, detailItem, pinMode, setPinMode, createPin, movePin, placingItem, setPlacingItem, placePin, zones, zoneMode, setZoneMode, createZone, updateZone, deleteZone, addDecks, renameDeck, deleteDeck, deckUploading, deckFileRef, exportSnagPdf, exporting, planIds, clearPlanIds, cropMode, setCropMode, cropDeck, lsaItems, showLsa, setShowLsa, autoPlaceLsa, mapping, moveLsa, placingLsa, setPlacingLsa, placeLsa }) {
  const ref = useRef(null)
  const mapRef = useRef(null)
  const pinLayerRef = useRef(null)
  const [mapReady, setMapReady] = useState(0)
  const [imgProblem, setImgProblem] = useState(null)   // why the deck's drawing did not open: shown over the plan
  const [imgTry, setImgTry] = useState(0)
  const pinModeRef = useRef(pinMode)
  const createRef = useRef(createPin)
  const placeRef = useRef({ placingItem, placePin })
  placeRef.current = { placingItem, placePin }
  useEffect(() => { pinModeRef.current = pinMode }, [pinMode])
  useEffect(() => { createRef.current = createPin })

  const [zoneFilter, setZoneFilter] = useState(null)
  const [editZone, setEditZone] = useState(null)
  const [resizing, setResizing] = useState(null)
  // "View plan" from a filtered list: only those items' pins
  const pins = allItems.filter(i => i.deck_id && i.pin_x != null && i.pin_y != null && (!planIds || planIds.has(i.id)))
  const deckPins = activeDeck
    ? pins.filter(i => i.deck_id === activeDeck.id && (!zoneFilter || i.zone_id === zoneFilter))
    : []

  const zoneLayerRef = useRef(null)
  const lsaLayerRef = useRef(null)
  const lsaRef = useRef({})
  const [pendingBox, setPendingBox] = useState(null)
  const [zoneName, setZoneName] = useState('')
  const zoneRef = useRef({})
  const [lsaPicker, setLsaPicker] = useState(false)
  const [pendingCrop, setPendingCrop] = useState(null)
  const [cropName, setCropName] = useState('')
  zoneRef.current = { zoneMode, zones, activeDeck, updateZone, deleteZone, cropMode, zoneFilter, setZoneFilter, setEditZone, resizing }
  const drawRef = useRef({})
  // the number on a pin is its row in the snag-list PDF: every pin of the deck, in list order
  const deckOrder = activeDeck ? allItems.filter(i => i.deck_id === activeDeck.id && i.pin_x != null).map(i => i.id) : []
  drawRef.current = { deckPins, activeDeck, detailItem, onPick, movePin, numberOf: id => deckOrder.indexOf(id) + 1 }
  lsaRef.current = { moveLsa, placingLsa, placeLsa }

  function drawZones() {
    const { zoneMode, zones, activeDeck } = zoneRef.current
    const map = mapRef.current, layer = zoneLayerRef.current
    if (!map || !layer || !activeDeck || !window.L) return
    const L = window.L
    layer.clearLayers()
    const W = activeDeck.width_px, H = activeDeck.height_px
    zones.filter(z => z.deck_id === activeDeck.id).forEach(z => {
      const b = [[(1 - (z.y + z.h)) * H, z.x * W], [(1 - z.y) * H, (z.x + z.w) * W]]
      const sel = zoneRef.current.zoneFilter === z.id
      const r = L.rectangle(b, {
        color: sel ? '#1B4FD8' : '#7C9BEA', weight: sel ? 2 : 1,
        fillColor: '#1B4FD8',
        fillOpacity: sel ? 0.12 : (zoneMode ? 0.08 : 0.03),
        interactive: true,
      })
      r.bindTooltip(z.name, { permanent: zoneMode, direction: 'center' })
      r.on('click', (e) => {
        L.DomEvent.stop(e)
        if (zoneMode) { zoneRef.current.setEditZone(z); return }
        if (pinModeRef.current || placeRef.current.placingItem || zoneRef.current.cropMode) return
        zoneRef.current.setZoneFilter(prev => prev === z.id ? null : z.id)
      })
      r.addTo(layer)
    })
  }

  function drawLsa() {
    const map = mapRef.current, layer = lsaLayerRef.current
    if (!map || !layer || !activeDeck || !window.L) return
    const L = window.L
    layer.clearLayers()
    if (!showLsa) return
    const W = activeDeck.width_px, H = activeDeck.height_px
    const today = todayLocal()
    lsaItems.filter(i => i.deck_id === activeDeck.id && i.pin_x != null).forEach(it => {
      const exp = it.expiry_date && it.expiry_date < today
      const col = exp ? '#C93B47' : '#137050'
      const html = '<div style="width:13px;height:13px;background:' + col +
        ';border:1.5px solid #fff;transform:rotate(45deg);box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>'
      const icon = L.divIcon({ html, className: 'hc-lsa', iconSize: [13, 13], iconAnchor: [6, 6] })
      const m = L.marker([(1 - it.pin_y) * H, it.pin_x * W], { icon, draggable: true, autoPan: true })
      m.bindTooltip((it.name || 'LSA') + (it.location ? ' \u00b7 ' + it.location : '') + (it.expiry_date ? ' \u00b7 exp ' + it.expiry_date : ''), { direction: 'top', offset: [0, -8] })
      m.on('dragend', (e) => {
        const ll = e.target.getLatLng()
        lsaRef.current.moveLsa(it.id, Math.min(1, Math.max(0, ll.lng / W)), Math.min(1, Math.max(0, 1 - (ll.lat / H))))
      })
      m.addTo(layer)
    })
  }

  const pinsDrawn = useRef(null)   // the deck whose pins have dropped in: a redraw (another pin picked) must not flash them all
  function drawPins() {
    const { deckPins, activeDeck, detailItem, onPick, movePin, numberOf } = drawRef.current
    const map = mapRef.current, layer = pinLayerRef.current
    if (!map || !layer || !activeDeck || !window.L) return
    const L = window.L
    layer.clearLayers()
    const W = activeDeck.width_px, H = activeDeck.height_px
    deckPins.forEach((p, pi) => {
      const color = pinColor(p)
      const sel = detailItem?.id === p.id
      if (!document.getElementById('hc-pin-css')) {
        const st = document.createElement('style'); st.id = 'hc-pin-css'
        st.textContent = '.hc-pin{background:none;border:none}' +
          '.hcp{box-sizing:border-box;width:28px;height:28px;border-radius:50%;background:var(--c);border:2.5px solid #fff;color:#fff;font:700 12px/1 Inter,sans-serif;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 7px rgba(11,23,48,.4)!important;transition:transform .15s ease}' +
          '.hcp:hover{transform:scale(1.14)}' +
          '.hcp.sel{transform:scale(1.2);box-shadow:0 0 0 3px rgba(27,79,216,.45),0 2px 7px rgba(11,23,48,.4)!important}' +
          '.hcp.in{animation:hcpDrop .3s ease-out both}' +
          '@keyframes hcpDrop{0%{transform:translateY(-8px);opacity:0}100%{transform:none;opacity:1}}' +
          '@media (prefers-reduced-motion:reduce){.hcp{animation:none!important;transition:none!important}}'
        document.head.appendChild(st)
      }
      const html = '<div class="hcp' + (p.done ? ' done' : '') + (sel ? ' sel' : '') + (pinsDrawn.current === activeDeck.id ? '' : ' in') + '" style="--c:' + color + '">' + (p.done ? '\u2713' : numberOf(p.id)) + '</div>'
      const icon = L.divIcon({ html, className: 'hc-pin', iconSize: [28, 28], iconAnchor: [14, 14] })
      const m = L.marker([(1 - p.pin_y) * H, p.pin_x * W], { icon, draggable: true, autoPan: true })
      m.bindTooltip(p.text || 'Task', { direction: 'top', offset: [0, -14] })
      m.on('click', () => onPick(p))
      m.on('dragend', (e) => {
        const ll = e.target.getLatLng()
        const nx = Math.min(1, Math.max(0, ll.lng / W))
        const ny = Math.min(1, Math.max(0, 1 - (ll.lat / H)))
        movePin(p.id, nx, ny)
      })
      m.addTo(layer)
    })
    pinsDrawn.current = activeDeck.id
  }

  useEffect(() => { setZoneFilter(null) }, [activeDeck?.id])

  useEffect(() => {
    if (!activeDeck || !ref.current) return
    let dead = false, resize = null
    setImgProblem(null)
    Promise.all([loadLeaflet(), signUrl(activeDeck.image_url)]).then(([L, signedSrc]) => {
      if (dead || !ref.current) return
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null }
      const W = activeDeck.width_px, H = activeDeck.height_px
      // smooth: quarter-step zoom on the wheel and pinch, a slower wheel, the pan keeps gliding
      const map = L.map(ref.current, { crs: L.CRS.Simple, minZoom: -4, maxZoom: 3, zoomControl: false, attributionControl: false, zoomSnap: 0.25, zoomDelta: 0.5, wheelPxPerZoomLevel: 140, inertiaDeceleration: 2600 })
      L.control.zoom({ position: 'topleft' }).addTo(map)   // bottom right sits under the AI button
      const bounds = [[0, 0], [H, W]]
      // private bucket: the signed link. No link, or the image does not open: say so on the plan (zones and pins still show)
      const overlay = L.imageOverlay(signedSrc || activeDeck.image_url, bounds).addTo(map)
      overlay.on('error', () => { if (!dead) setImgProblem(planImageProblem(activeDeck)) })
      map.fitBounds(bounds, { padding: window.innerWidth < 761 ? [6, 6] : [24, 24], animate: false })
      if (window.ResizeObserver) { resize = new ResizeObserver(() => { if (!dead) map.invalidateSize({ pan: false }) }); resize.observe(ref.current) }
      map.on('click', (e) => {
        const pl = lsaRef.current.placingLsa
        if (pl) {
          const px = e.latlng.lng / W, py = 1 - (e.latlng.lat / H)
          if (px < 0 || px > 1 || py < 0 || py > 1) return
          lsaRef.current.placeLsa(activeDeck.id, px, py)
          return
        }
        const placing = placeRef.current.placingItem
        if (!pinModeRef.current && !placing) return
        const px = e.latlng.lng / W
        const py = 1 - (e.latlng.lat / H)
        if (px < 0 || px > 1 || py < 0 || py > 1) return
        if (placing) { placeRef.current.placePin(activeDeck.id, px, py); return }
        createRef.current(activeDeck.id, px, py)
        setPinMode(false)
      })
      zoneLayerRef.current = L.layerGroup().addTo(map)
      lsaLayerRef.current = L.layerGroup().addTo(map)
      pinLayerRef.current = L.layerGroup().addTo(map)
      mapRef.current = map
      setMapReady(v => v + 1)
      drawZones()
      drawLsa()
      drawPins()

      let start = null, ghost = null
      map.on('mousedown', (e) => {
        if (!zoneRef.current.zoneMode && !zoneRef.current.cropMode) return
        map.dragging.disable()
        start = e.latlng
      })
      map.on('mousemove', (e) => {
        if (!start) return
        if (ghost) ghost.remove()
        ghost = L.rectangle([start, e.latlng], { color: '#1B4FD8', weight: 2, dashArray: '5,4', fillOpacity: 0.08 }).addTo(map)
      })
      map.on('mouseup', (e) => {
        if (!start) return
        map.dragging.enable()
        if (ghost) { ghost.remove(); ghost = null }
        const a = start; start = null
        const x1 = Math.min(a.lng, e.latlng.lng) / W, x2 = Math.max(a.lng, e.latlng.lng) / W
        const y1 = 1 - (Math.max(a.lat, e.latlng.lat) / H), y2 = 1 - (Math.min(a.lat, e.latlng.lat) / H)
        const box = { x: Math.max(0, x1), y: Math.max(0, y1), w: Math.min(1, x2 - x1), h: Math.min(1, y2 - y1) }
        if (box.w < 0.005 || box.h < 0.01) return
        const rz = zoneRef.current.resizing
        if (rz) { zoneRef.current.updateZone(rz.id, box); setResizing(null); setEditZone(null); return }
        if (zoneRef.current.cropMode) { setPendingCrop(box); return }
        setPendingBox(box)
      })
      setTimeout(() => { if (!dead) map.invalidateSize() }, 60)
    }).catch(() => { if (!dead) setImgProblem(planImageProblem(activeDeck)) })
    return () => { dead = true; if (resize) resize.disconnect(); if (mapRef.current) { mapRef.current.stop(); mapRef.current.remove(); mapRef.current = null } }
  }, [activeDeck?.id, imgTry])

  useEffect(() => {
    drawPins()
  }, [mapReady, deckPins.length, activeDeck?.id, detailItem?.id, allItems, planIds])

  useEffect(() => {
    drawZones()
  }, [mapReady, zones, zoneMode, activeDeck?.id, zoneFilter])

  useEffect(() => {
    drawLsa()
  }, [mapReady, showLsa, lsaItems, activeDeck?.id])

  const deckInput = (
    <input ref={deckFileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }}
      onChange={e => { addDecks(Array.from(e.target.files || [])); e.target.value = '' }} />
  )

  if (!decks.length) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>No GA plan yet</div>
      <div style={{ fontSize: 13, marginBottom: 18, lineHeight: 1.6 }}>
        Upload your GA drawing as a PDF, or one image per deck.<br />Each PDF page becomes a plan.<br />
        The file name becomes the deck name — you can rename it after.
      </div>
      <button className="td-primary" onClick={() => deckFileRef.current?.click()} disabled={deckUploading} style={{
        padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--blue)',
        color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
      }}>{deckUploading ? 'Uploading…' : '+ Upload GA plan'}</button>
      {deckInput}
    </div>
  )

  const openCount = deckPins.filter(p => !p.done).length
  const unplaced = allItems.filter(i => !i.done && i.deck_id && i.pin_x == null)
  const lsaUnplaced = lsaItems.filter(i => i.pin_x == null)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
        {decks.map(d => {
          const n = pins.filter(p => p.deck_id === d.id && !p.done).length
          const on = activeDeck?.id === d.id
          return (
            <button key={d.id} className="td-btn" onClick={() => setActiveDeck(d)} style={{
              padding: '5px 11px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
              border: '1px solid ' + (on ? 'rgba(27,79,216,.32)' : 'var(--line-2)'),
              background: on ? 'rgba(27,79,216,.08)' : 'var(--card)',
              color: on ? 'var(--blue)' : 'var(--muted)', fontWeight: on ? 600 : 500,
            }}>
              {d.name}{n > 0 ? ' \u00b7 ' + n : ''}
            </button>
          )
        })}
        <div style={{ flex: 1 }} />
        {zoneFilter && (
          <button className="td-btn" onClick={() => setZoneFilter(null)} style={{
            padding: '5px 11px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600,
            border: '1px solid rgba(27,79,216,.32)', background: 'rgba(27,79,216,.08)', color: 'var(--blue)',
          }}>{zones.find(z => z.id === zoneFilter)?.name} · {deckPins.length} ×</button>
        )}
        {unplaced.length > 0 && (
          <button className="td-btn" onClick={() => { setPlacingItem(unplaced[0]); onPick(unplaced[0]) }} style={{
            padding: '5px 11px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600,
            border: '1px solid rgba(27,79,216,.32)', background: 'rgba(27,79,216,.08)', color: 'var(--blue)',
          }}>Unplaced · {unplaced.length}</button>
        )}
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{openCount} open</span>
        <button className="td-btn" onClick={() => setShowLsa(v => !v)} title="Show LSA / FFE equipment" style={{
          padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: showLsa ? 600 : 400,
          border: '1px solid ' + (showLsa ? '#137050' : 'var(--line-2)'),
          background: showLsa ? 'rgba(23,138,92,.12)' : 'transparent',
          color: showLsa ? '#137050' : 'var(--muted)',
        }}>LSA {showLsa ? '\u2713' : ''}</button>
        {showLsa && (
          <button className="td-btn" onClick={autoPlaceLsa} disabled={mapping} title="Match LSA locations to zones with AI" style={{
            padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
            border: '1px solid var(--line-2)', background: 'transparent', color: 'var(--muted)',
          }}>{mapping ? 'Matching\u2026' : 'Auto-place'}</button>
        )}
        {showLsa && lsaUnplaced.length > 0 && (
          <button className="td-btn" onClick={() => setLsaPicker(true)} style={{
            padding: '5px 11px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600,
            border: '1px solid rgba(27,79,216,.32)', background: 'rgba(27,79,216,.08)', color: 'var(--blue)',
          }}>Unplaced LSA · {lsaUnplaced.length}</button>
        )}
        <button className="td-btn" onClick={() => { setCropMode(v => !v); setZoneMode(false); setPinMode(false) }} title="Cut a deck out of this plan" style={{
          padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: cropMode ? 600 : 400,
          border: '1px solid ' + (cropMode ? '#1B4FD8' : 'var(--line-2)'),
          background: cropMode ? '#1B4FD8' : 'transparent', color: cropMode ? '#fff' : 'var(--muted)',
        }}>{cropMode ? 'Done' : 'Crop deck'}</button>
        <button className="td-btn" onClick={exportSnagPdf} disabled={exporting} title="Export snag list PDF" style={{
          padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
          border: '1px solid var(--line-2)', background: 'var(--card)', color: 'var(--muted)',
        }}>{exporting ? 'Exporting…' : 'Export PDF'}</button>
        <button className="td-btn" onClick={() => deckFileRef.current?.click()} disabled={deckUploading} title="Add a deck plan" style={{
          padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
          border: '1px dashed var(--line-2)', background: 'transparent', color: 'var(--muted)',
        }}>{deckUploading ? '…' : '+ Plan'}</button>
        {deckInput}
        {activeDeck && (zoneMode || cropMode) && (
          <>
            <button className="td-btn" onClick={() => { const n = prompt('Deck name', activeDeck.name); if (n && n.trim()) renameDeck(activeDeck.id, n.trim()) }}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: '1px solid var(--line-2)', background: 'transparent', color: 'var(--muted)' }}>Rename</button>
            <button className="td-btn" onClick={() => { if (confirm('Delete the ' + activeDeck.name + ' plan? Pins on it will be unpinned.')) deleteDeck(activeDeck.id) }}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: '1px solid var(--line-2)', background: 'transparent', color: '#C93B47' }}>Delete plan</button>
          </>
        )}
        <button className="td-btn" onClick={() => { setZoneMode(v => !v); setPinMode(false) }} style={{
          padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600,
          border: '1px solid ' + (zoneMode ? '#1B4FD8' : 'var(--line-2)'),
          background: zoneMode ? '#1B4FD8' : 'transparent',
          color: zoneMode ? '#fff' : 'var(--muted)',
        }}>{zoneMode ? 'Done' : 'Edit zones'}</button>
        <button className="td-primary" onClick={() => setPinMode(v => !v)} style={{
          padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600,
          border: '1px solid ' + (pinMode ? '#C93B47' : 'var(--blue)'),
          background: pinMode ? '#C93B47' : 'var(--blue)',
          color: '#fff',
        }}>{pinMode ? 'Cancel' : '+ Drop pin'}</button>
      </div>
      {planIds && (
        <div style={{ padding: '7px 14px', fontSize: 12, color: 'var(--text)', background: 'rgba(27,79,216,.06)', display:'flex', alignItems:'center', gap:10 }}>
          <span>Showing the {pins.length} pinned item{pins.length === 1 ? '' : 's'} of the filtered list</span>
          <button style={{ background:'none', border:'none', color:'var(--blue)', cursor:'pointer', fontSize:12, fontWeight:600 }} onClick={clearPlanIds}>Show all pins</button>
        </div>
      )}
      {placingItem && (
        <div style={{ padding: '7px 14px', fontSize: 12, color: 'var(--text)', background: 'rgba(27,79,216,.08)', display:'flex', alignItems:'center', gap:10 }}>
          <span>Pick the deck tab, then click the plan to pin <b>{placingItem.text}</b></span>
          <button style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:12 }} onClick={() => setPlacingItem(null)}>Cancel</button>
        </div>
      )}
      {cropMode && <div style={{ padding: '6px 14px', fontSize: 12, color: 'var(--muted)', background: 'rgba(27,79,216,.08)' }}>Drag a box around one deck on the drawing — it becomes its own plan.</div>}
      {placingLsa && (
        <div style={{ padding: '7px 14px', fontSize: 12, color: 'var(--text)', background: 'rgba(23,138,92,.14)', display:'flex', alignItems:'center', gap:10 }}>
          <span>Pick the deck tab, then click the plan to place <b>{placingLsa.name}</b>{placingLsa.location ? ' (' + placingLsa.location + ')' : ''}</span>
          <button style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:12 }} onClick={() => setPlacingLsa(null)}>Cancel</button>
        </div>
      )}
      {zoneMode && <div style={{ padding: '6px 14px', fontSize: 12, color: 'var(--muted)', background: 'rgba(27,79,216,.08)' }}>Drag a box around a space to name it. Click a zone to delete it.</div>}

      {lsaPicker && (
        <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(10,22,40,.55)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setLsaPicker(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--card)', borderRadius:14, width:520, maxWidth:'94%', maxHeight:'80vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 70px rgba(0,0,0,.35)' }}>
            <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--line)' }}>
              <div style={{ fontSize:15, fontWeight:700, color:'var(--text)' }}>Unplaced LSA / FFE</div>
              <div style={{ fontSize:12, color:'var(--muted)', marginTop:3 }}>Pick an item, then click the plan to place it.</div>
            </div>
            <div style={{ overflowY:'auto', padding:'4px 20px 10px' }}>
              {lsaUnplaced.map(it => (
                <div key={it.id} onClick={() => { setPlacingLsa(it); setLsaPicker(false) }}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid var(--line)', cursor:'pointer' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{it.name}</div>
                    <div style={{ fontSize:11, color:'var(--muted)' }}>{it.category || ''}{it.location ? ' \u00b7 ' + it.location : ''}</div>
                  </div>
                  <span style={{ fontSize:11, color:'var(--blue)', fontWeight:600 }}>Place</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {resizing && (
        <div style={{ padding:'7px 14px', fontSize:12, color:'var(--text)', background:'rgba(27,79,216,.08)', display:'flex', alignItems:'center', gap:10 }}>
          <span>Drag a new box for <b>{resizing.name}</b></span>
          <button style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:12 }} onClick={() => setResizing(null)}>Cancel</button>
        </div>
      )}

      {editZone && !resizing && (
        <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(10,22,40,.5)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setEditZone(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--card)', borderRadius:14, padding:18, width:320, maxWidth:'92%', boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize:14, fontWeight:700, marginBottom:14, color:'var(--text)' }}>{editZone.name}</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <button onClick={() => { const n = prompt('Zone name', editZone.name); if (n && n.trim()) updateZone(editZone.id, { name: n.trim() }); setEditZone(null) }}
                style={{ padding:'9px 12px', borderRadius:9, fontSize:13, cursor:'pointer', textAlign:'left', border:'1px solid var(--line)', background:'transparent', color:'var(--text)' }}>Rename</button>
              <button onClick={() => setResizing(editZone)}
                style={{ padding:'9px 12px', borderRadius:9, fontSize:13, cursor:'pointer', textAlign:'left', border:'1px solid var(--line)', background:'transparent', color:'var(--text)' }}>Resize — drag a new box</button>
              <button onClick={() => { if (confirm('Delete zone "' + editZone.name + '"? You can undo it for 10 seconds.')) { deleteZone(editZone.id); setEditZone(null) } }}
                style={{ padding:'9px 12px', borderRadius:9, fontSize:13, cursor:'pointer', textAlign:'left', border:'1px solid rgba(201,59,71,.35)', background:'transparent', color:'#C93B47' }}>Delete zone</button>
            </div>
          </div>
        </div>
      )}

      {pendingCrop && (
        <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(10,22,40,.5)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => { setPendingCrop(null); setCropName('') }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--card)', borderRadius:14, padding:18, width:360, maxWidth:'92%', boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:'var(--text)' }}>Name this deck</div>
            <div style={{ display:'flex', gap:8 }}>
              <input autoFocus value={cropName} onChange={e => setCropName(e.target.value)} placeholder="e.g. Lower Deck"
                onKeyDown={e => { if (e.key === 'Enter' && cropName.trim()) { cropDeck(activeDeck, pendingCrop, cropName.trim()); setPendingCrop(null); setCropName('') } }}
                style={{ flex:1, padding:'8px 10px', borderRadius:8, border:'1px solid var(--line)', background:'transparent', color:'var(--text)', fontSize:13 }} />
              <button className="td-primary" onClick={() => { if (cropName.trim()) { cropDeck(activeDeck, pendingCrop, cropName.trim()); setPendingCrop(null); setCropName('') } }}
                style={{ padding:'8px 14px', borderRadius:8, border:'none', background:'var(--blue)', color:'#fff', fontWeight:600, fontSize:12, cursor:'pointer' }}>Create</button>
            </div>
          </div>
        </div>
      )}
      {pinMode && !placingItem && <div style={{ padding: '6px 14px', fontSize: 12, color: 'var(--muted)', background: 'rgba(27,79,216,.08)' }}>Click the plan to place the pin.</div>}

      {pendingBox && (
        <div style={{ position:'fixed', inset:0, zIndex:9998, background:'rgba(10,22,40,.5)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => { setPendingBox(null); setZoneName('') }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--card)', borderRadius:14, padding:18, width:380, maxWidth:'92%', boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:'var(--text)' }}>Name this zone</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12, maxHeight:190, overflowY:'auto' }}>
              {paletteFor(activeDeck?.name).map(nm => (
                <button key={nm} onClick={() => { createZone(activeDeck.id, nm, pendingBox); setPendingBox(null); setZoneName('') }}
                  style={{ padding:'5px 10px', borderRadius:7, fontSize:12, cursor:'pointer', border:'1px solid var(--line)', background:'transparent', color:'var(--text)' }}>{nm}</button>
              ))}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <input autoFocus value={zoneName} onChange={e => setZoneName(e.target.value)} placeholder="Or type a name…"
                onKeyDown={e => { if (e.key === 'Enter' && zoneName.trim()) { createZone(activeDeck.id, zoneName.trim(), pendingBox); setPendingBox(null); setZoneName('') } }}
                style={{ flex:1, padding:'8px 10px', borderRadius:8, border:'1px solid var(--line)', background:'transparent', color:'var(--text)', fontSize:13 }} />
              <button className="td-primary" onClick={() => { if (zoneName.trim()) { createZone(activeDeck.id, zoneName.trim(), pendingBox); setPendingBox(null); setZoneName('') } }}
                style={{ padding:'8px 14px', borderRadius:8, border:'none', background:'var(--blue)', color:'#fff', fontWeight:600, fontSize:12, cursor:'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
      {imgProblem && (
        <div className="tdc-plan-msg" role="alert">
          <span><b>{imgProblem.title}</b> {imgProblem.reason} Zones and pins are still shown. <span className="code">({imgProblem.code})</span></span>
          <button type="button" className="tdf-clear" onClick={() => { forgetFile(activeDeck.image_url); setImgTry(v => v + 1) }}>Try again</button>
        </div>
      )}
      <div className="tdc-plan-wrap">
        <div ref={ref} className="tdc-plan-map" style={{ cursor: (pinMode || placingItem || cropMode) ? 'crosshair' : 'grab' }} />
        <div className="tdc-legend" aria-label="Pins on this deck"><span><u style={{ background: pinColor({ done: false }) }} />{deckPins.filter(p => !p.done).length} open</span><span><u style={{ background: pinColor({ done: true }) }} />{deckPins.filter(p => p.done).length} closed</span></div>
      </div>
    </div>
  )
}

function CalendarView({ allItems, calMonth, setCalMonth, onPick, listName }) {
  const { y, m } = calMonth
  const first = new Date(y, m, 1)
  const startDay = first.getDay() // 0=Sun
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const monthName = first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  const cells = []
  for (let i = 0; i < startDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  const todayS = todayLocal()
  function tasksOn(d) {
    const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    return allItems.filter(i => i.due_date === ds)
  }
  function prev() { setCalMonth(m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }) }
  function next() { setCalMonth(m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }) }
  return (
    <div style={cs.wrap}>
      <div style={cs.head}>
        <button className="td-btn" style={cs.nav} onClick={prev}>‹</button>
        <div style={cs.month}>{monthName}</div>
        <button className="td-btn" style={cs.nav} onClick={next}>›</button>
        <button className="td-btn" style={cs.today} onClick={() => { const t = new Date(); setCalMonth({ y: t.getFullYear(), m: t.getMonth() }) }}>Today</button>
      </div>
      <div style={cs.grid}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} style={cs.dow}>{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={'e' + i} style={cs.empty} />
          const ds = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
          const tasks = tasksOn(d)
          const isToday = ds === todayS
          return (
            <div key={d} style={{ ...cs.cell, ...(isToday ? cs.cellToday : {}) }}>
              <div style={{ ...cs.dayNum, ...(isToday ? cs.dayNumToday : {}) }}>{d}</div>
              <div style={cs.cellTasks}>
                {tasks.slice(0, 3).map(t => {
                  const pr = PRIORITY[t.priority || 0]
                  return <div key={t.id} style={{ ...cs.pill, ...(t.done ? cs.pillDone : {}), borderLeft: `3px solid ${pr.color}` }} onClick={() => onPick(t)} title={t.text}>{t.text}</div>
                })}
                {tasks.length > 3 && <div style={cs.more}>+{tasks.length - 3} more</div>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function Dropdown({ value, options, placeholder, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const selected = options.find(o => o.value === value)
  return (
    <div ref={ref} style={ds.wrap}>
      <button className="td-select" style={ds.trigger} onClick={() => setOpen(o => !o)}>
        <span style={{ color: selected ? 'var(--text)' : 'var(--muted)' }}>{selected ? selected.label : placeholder}</span>
        <span style={{ ...ds.caret, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && (
        <div style={ds.menu}>
          {options.map(o => (
            <div key={o.value} className="td-menu-item" style={{ ...ds.opt, ...(o.value === value ? ds.optSel : {}) }} onClick={() => { onChange(o.value); setOpen(false) }}>
              {o.color && <span style={{ ...ds.dot, background: o.color }} />}
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ds = {
  wrap: { position: 'relative', width: '100%' },
  trigger: { width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 9, padding: '8px 11px', color: 'var(--text)', fontSize: 13, cursor: 'pointer' },
  caret: { color: 'var(--muted)', fontSize: 10, transition: 'transform .15s' },
  calIcon: { fontSize: 12, opacity: 0.7 },
  menu: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 10, padding: 4, maxHeight: 220, overflowY: 'auto' },
  opt: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, color: 'var(--text)', fontSize: 13, cursor: 'pointer' },
  optSel: { background: 'rgba(27,79,216,.08)', color: 'var(--blue)', fontWeight: 600 },
  dot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
}

const cs = {
  wrap: { display: 'flex', flexDirection: 'column', height: '100%' },
  head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  nav: { background: 'var(--card)', border: '1px solid var(--line-2)', color: 'var(--text)', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: 18, lineHeight: 1 },
  month: { fontSize: 18, fontWeight: 700, color: 'var(--text)', minWidth: 180 },
  today: { marginLeft: 'auto', background: 'var(--card)', color: 'var(--blue)', border: '1px solid var(--line-2)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12 },
  grid: { flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: 'minmax(80px, 1fr)', gap: 4, overflowY: 'auto' },
  dow: { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textAlign: 'center', padding: '4px 0', textTransform: 'uppercase' },
  empty: { background: 'transparent' },
  cell: { background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 9, padding: 5, display: 'flex', flexDirection: 'column', gap: 3, minHeight: 76, overflow: 'hidden' },
  cellToday: { borderColor: 'var(--blue)', background: 'rgba(27,79,216,.04)' },
  dayNum: { fontSize: 12, color: 'var(--muted)', fontWeight: 600 },
  dayNumToday: { color: 'var(--blue)' },
  cellTasks: { display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' },
  pill: { fontSize: 10, color: 'var(--text)', background: 'var(--bg)', borderRadius: 3, padding: '2px 4px', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pillDone: { textDecoration: 'line-through', color: 'var(--muted)', opacity: 0.6 },
  more: { fontSize: 9, color: 'var(--muted)', padding: '0 4px' },
}

const s = {
  viewScroll: { flex: 1, overflowY: 'auto', padding: 6, minHeight: 0 },
  section: { padding: '10px 10px 5px', fontSize: 12, fontWeight: 600, color: 'var(--muted)' },
  viewRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', borderRadius: 9, cursor: 'pointer', color: 'var(--text)', fontSize: 14 },
  viewRowActive: { background: 'rgba(27,79,216,.09)', color: 'var(--blue)', fontWeight: 600 },
  listName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge: { fontSize: 12, fontWeight: 600, color: 'var(--muted)', minWidth: 18, textAlign: 'right' },
  listDel: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' },
  newListRow: { display: 'flex', gap: 6, padding: '6px 8px' },
  newListInput: { flex: 1, minWidth: 0, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 8, padding: '7px 9px', color: 'var(--text)', fontSize: 13 },
  addListBtn: { background: 'rgba(27,79,216,.08)', color: 'var(--blue)', border: '1px solid rgba(27,79,216,.2)', borderRadius: 8, width: 32, cursor: 'pointer', fontSize: 18, fontWeight: 700 },
  filterChip: { background: 'var(--card)', border: '1px solid var(--line-2)', color: 'var(--text)', borderRadius: 8, height: 32, padding: '0 11px', cursor: 'pointer', fontSize: 12.5, fontWeight: 500 },
  primaryChip: { background: 'var(--blue)', border: '1px solid var(--blue)', color: '#fff', borderRadius: 8, height: 32, padding: '0 13px', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 },
  menu: { position: 'absolute', top: '120%', zIndex: 60, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 10, padding: 5, display: 'flex', flexDirection: 'column', minWidth: 190 },
  menuBtn: { background: 'none', border: 'none', color: 'var(--text)', fontSize: 12.5, textAlign: 'left', padding: '7px 9px', borderRadius: 7, cursor: 'pointer' },
  bulkBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', marginBottom: 8, borderRadius: 10, background: 'rgba(27,79,216,.05)', border: '1px solid rgba(27,79,216,.18)' },
  bulkPanel: { padding: 12, marginBottom: 12, borderRadius: 12, background: 'var(--card)', border: '1px solid var(--line-2)' },
  statusPill: { fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '2px 8px', flexShrink: 0 },
  statusOpen: { color: '#fff', background: '#C93B47' },
  statusClosed: { color: '#fff', background: '#137050' },
  addBtn: { background: 'var(--blue)', color: '#fff', border: '1px solid var(--blue)', borderRadius: 9, height: 34, padding: '0 16px', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, flexShrink: 0 },
  items: { display: 'block' },
  itemTextDone: { textDecoration: 'line-through', color: 'var(--muted)' },
  emptyHint: { color: 'var(--muted)', fontSize: 13, padding: '16px 4px', textAlign: 'center' },
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px 0', fontSize: 13, flexShrink: 0 },
  detailClose: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 22, lineHeight: 1 },
  field: { marginBottom: 14 },
  fieldLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 5 },
  fieldInput: { width: '100%', boxSizing: 'border-box', backgroundColor: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 9, padding: '8px 11px', color: 'var(--text)', fontSize: 13 },
  prRow: { display: 'flex', gap: 6 },
  fieldArea: { width: '100%', boxSizing: 'border-box', minHeight: 80, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 9, padding: '8px 11px', color: 'var(--text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' },
  dropTarget: { background: 'rgba(27,79,216,.08)', outline: '2px dashed var(--blue)', outlineOffset: -2 },
  subRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--line)' },
  subText: { flex: 1, fontSize: 13, color: 'var(--text)' },
  subDel: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '0 4px' },
  subAdd: { display: 'flex', gap: 6, marginTop: 8 },
  subIn: { flex: 1, minWidth: 0, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 8, color: 'var(--text)', fontSize: 13, padding: '7px 10px' },
  subBtn: { background: 'var(--blue)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 16, padding: '0 12px', cursor: 'pointer' },
  detailDel: { background: 'none', color: '#C93B47', border: 'none', borderRadius: 8, padding: '6px 8px', marginLeft: -8, cursor: 'pointer', fontSize: 13.5, fontWeight: 600 },
}
